import {
  Address,
  ApiKey,
  BankAccount,
  Brand,
  CheckoutCustomizationSettings,
  CommercialInvoiceFullData,
  CommercialInvoiceStatus,
  CompanyBankPaymentsSettings,
  CompanyCustomizationSettings,
  CompanyOfficer,
  CompanySettings,
  CreateEntities,
  CreateSegment,
  CreateStripeSubscriptionDTO,
  CurrencyCodes,
  CurrencyCodesType,
  CurrencySettings,
  Customer,
  CustomerAddress,
  CustomerExternalAppAccessStatus,
  EmailCustomizationSettings,
  ExternalIntegration,
  ExternalIntegrationTypes,
  MFAMethod,
  Note,
  Order,
  OrderStatus,
  Partner_CreatedCompany,
  Partner_CreateManagedCompanyIntegration,
  PartnerDashboardCustomizationSettings,
  PayeeAccount,
  Payment,
  PaymentProviders,
  PaymentStatus,
  PaymentToolOptions,
  PdfCustomizationSettings,
  PersonEntityType,
  PickupPoint,
  Product,
  ProductStatus,
  RecurringPayment,
  RecurringPaymentStatus,
  Reminder,
  ReminderParentEntityType,
  Role,
  SalesChannel,
  SegmentType,
  StripePaymentStatus,
  SyncDirection,
  UpdateCommercialInvoice,
  UpdateSegment,
} from "@fena/toolkit-types";
import { Auth } from "aws-amplify";
import { isArray } from "lodash";
import { stringify } from "querystring";
import { publicPaths } from "../constants/publicPaths";
import { TrackDeskConfig } from "../context/AffiliateSignupsContextProvider";
import {
  CreateInventoryOrderBody,
  InventoryOrderProductBody,
  NoteBody,
  UpdateInventoryOrderBody,
} from "../modules/private/inventory-module/incoming-inventory/types";
import { OptionFormValues } from "../modules/private/inventory-module/products/add-new/components/OptionsControl";
import { InvoicesFilters } from "../modules/private/invoices-module/hooks/useGetInvoices";
import { EditCompanyDetailsFormValues } from "../modules/private/partner/merchants/details/components/EditDetailsModal";
import { MerchantBankFormValues } from "../modules/private/partner/types";
import { LocationsFilters } from "../modules/private/settings-module/locations/hooks/useGetLocations";
import { ManifestFilters } from "../modules/private/shipments-module/manifest-history/hooks/useGetManifests";
import { appendNestedObjectToFormData } from "./data-utils";
import {
  type ApiKeyApiType,
  type BankAccountApiType,
  type BrandApiType,
  BulkPaymentApiType,
  type CategoryApiType,
  CINonMandatoryFields,
  type CompanyApiType,
  CreateInvoiceBody,
  CreateOrderBody,
  CreateStripeUserAddonQuery,
  type CreateSupplierBody,
  type CustomerAddressBody,
  type CustomerCreateBody,
  InvoiceFullData,
  type LocationApiType,
  MarkAsPaidInvoiceBody,
  OrderProductBody,
  type PaymentApiType,
  PaymentProvidersCheckout,
  PdfResponseFormat,
  type PickUpPointApiType,
  ProcessOrderBody,
  ProductVariantApiType,
  type ProviderApiType,
  ShipEnginePackage,
  type ShippingCarrierApiType,
  IndividualPaymentApiType,
  type UserApiType,
  PaginatedResponse,
  ExternalAppApiType,
} from "./types";

const apiUrl = process.env.REACT_APP_PUBLIC_API_URL as string;
// const apiUrl = "http://localhost:8084/";

class Api {
  private readonly mainUrl: string;
  private readonly defaultHeaders: Headers;

  constructor(url: string) {
    this.mainUrl = url;
    this.defaultHeaders = new Headers();
    this.defaultHeaders.set("Content-type", "application/json");
    if (typeof window !== "undefined") {
      const token = window.localStorage.getItem("accesstoken");
      if (token) this.defaultHeaders.set("accesstoken", token);

      const selectedCompany = window.localStorage.getItem("selectedCompany");
      if (selectedCompany)
        this.defaultHeaders.set("selectedcompany", selectedCompany);
    }
  }

  async renewToken() {
    const session = await Auth.currentSession();
    const accessToken = session.getIdToken();
    const jwt = accessToken.getJwtToken();
    this.updateHeadersWithToken(jwt);
  }

  async fetcher(url: string, init: Record<string, any>) {
    const urlObj = new URL(url);

    const isPublicPath = publicPaths.some((path) =>
      urlObj.pathname.startsWith(path)
    );

    if (!isPublicPath) {
      const token = window.localStorage.getItem("accesstoken");

      if (!token) {
        try {
          await this.renewToken();
        } catch (e) {
          throw {
            status: 401,
            error: { message: "Token missing and cannot renew session" },
          };
        }
      }
    }

    const result = await fetch(url, init);
    if (!result.ok) {
      if (result.status === 401) {
        await this.renewToken();
        const retryResult = await fetch(url, init);
        if (!retryResult.ok && retryResult.status === 401) {
          const error = await result.json();
          throw { status: result.status, error };
        } else {
          return retryResult;
        }
      }
      // if (result.status === 403) {
      //   await router.push({ pathname: '/login/suspended' });
      // }

      const error = await result.json();
      throw { status: result.status, error };
    }
    return result;
  }

  updateHeadersWithToken(token: string) {
    this.defaultHeaders.set("accesstoken", token);
  }

  updateHeadersWithSelectedCompany(companyId: string) {
    this.defaultHeaders.set("selectedcompany", companyId);
  }

  async getUser(): Promise<UserApiType> {
    const url = new URL(this.mainUrl + "internal/user/data");
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async updateLastLogin(): Promise<UserApiType> {
    const url = new URL(this.mainUrl + "internal/user/updateLastLogin");
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getCompany() {
    const url = new URL(this.mainUrl + "internal/company/info/get");
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  /* Company Customization Settings */
  async getCompanyCustomizationSettings(): Promise<CompanyCustomizationSettings> {
    const url = new URL(
      `${this.mainUrl}internal/company/customization-settings`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getCanUseDomain(domain: string) {
    const url = new URL(
      `${this.mainUrl}internal/company/customization-settings/can-use-domain`
    );
    url.searchParams.append("domain", domain);
    const response = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const { result } = await response.json();
    return result.canUseDomain;
  }

  async getParentCustomizationSettings() {
    const url = new URL(
      `${this.mainUrl}internal/company/customization-settings/parent-settings`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const { data } = await result.json();
    return data;
  }

  async getParentCustomizationSettingsForForgotPassword(email: string) {
    const url = new URL(
      `${this.mainUrl}public/public-data/company/customization-settings/parent-settings-forgot-password/${email}`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const { data } = await result.json();
    return data;
  }

  async getCustomizationSettingsForLanding(domain: string) {
    const url = new URL(
      `${this.mainUrl}public/public-data/company/customization-settings/landing/${domain}`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const { data } = await result.json();
    return data;
  }

  async updatePartnerDashboardSettings(
    partnerDashboardSettings: PartnerDashboardCustomizationSettings
  ) {
    const url = new URL(
      `${this.mainUrl}internal/company/customization-settings/partner-dashboard`
    );

    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      body: JSON.stringify({ ...partnerDashboardSettings }),
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async updateEmailCustomizationSettings(
    emailSettingsData: EmailCustomizationSettings
  ) {
    const url = new URL(
      `${this.mainUrl}internal/company/customization-settings/email`
    );

    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      body: JSON.stringify({ ...emailSettingsData }),
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async updateCheckoutCustomizationSettings(
    checkoutSettingsData: Omit<CheckoutCustomizationSettings, "isEnabled">
  ) {
    const url = new URL(
      `${this.mainUrl}internal/company/customization-settings/checkout`
    );

    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      body: JSON.stringify({ ...checkoutSettingsData }),
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async updatePdfCustomizationSettings(
    pdfSettingsData: PdfCustomizationSettings
  ) {
    const url = new URL(
      `${this.mainUrl}internal/company/customization-settings/pdf`
    );

    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      body: JSON.stringify({ ...pdfSettingsData }),
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async attachCustomizationLogo(
    attachFor:
      | "/email/logo/header"
      | "/email/logo/footer"
      | "/pdf/logo"
      | "/checkout/logo"
      | "/partner-dashboard/logo"
      | "/partner-dashboard/faviconLogo",
    attachment: File
  ) {
    const fd = new FormData();
    fd.append("file", attachment);
    const modifiedHeaders = new Headers(this.defaultHeaders);
    modifiedHeaders.delete("Content-Type");
    const url = new URL(
      `${this.mainUrl}internal/company/customization-settings${attachFor}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: modifiedHeaders,
      body: fd,
    });
    return await result.json();
  }

  async deleteCustomizationLogo(
    fieldName:
      | "email.headerLogo"
      | "email.footerLogo"
      | "checkout.headerLogo"
      | "pdf.logo"
      | "partner_dashboard.logo"
      | "partner_dashboard.faviconLogo"
  ) {
    const url = new URL(
      `${this.mainUrl}internal/company/customization-settings/delete-logo`
    );
    const result = await this.fetcher(url.toString(), {
      method: "DELETE",
      headers: this.defaultHeaders,
      body: JSON.stringify({ fieldName }),
    });
    return await result.json();
  }

  async sendTestEmail(email: string) {
    const url = new URL(
      `${this.mainUrl}internal/company/customization-settings/test-email`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify({ email }),
    });
    return await result.json();
  }

  async toggleCheckoutSettings() {
    const url = new URL(
      `${this.mainUrl}internal/company/customization-settings/checkout/toggle`
    );
    const result = await this.fetcher(url.toString(), {
      method: "PATCH",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async getCompanyFenaCheckoutCustomizationSettings(): Promise<CompanyBankPaymentsSettings> {
    const url = new URL(`${this.mainUrl}internal/company/settings/cards`);

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async updateFenaCheckoutCustomizationSettings(
    fenaCheckoutCustomizationSettingsData: CompanyBankPaymentsSettings
  ): Promise<CompanyBankPaymentsSettings> {
    const url = new URL(
      `${this.mainUrl}internal/company/settings/cards-update`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify(fenaCheckoutCustomizationSettingsData),
    });
    const data = await result.json();
    return data.data;
  }
  /* END Company Customization Settings */

  async createUser(userData: Partial<UserApiType>) {
    const url = new URL(this.mainUrl + "public/sign-up/create");
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(userData),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async updateUser(
    userData: Partial<UserApiType> & { deleteFile?: any },
    file?: File | null
  ) {
    const url = new URL(this.mainUrl + "internal/user/update");
    const fd = new FormData();
    appendNestedObjectToFormData(fd, userData);
    if (file) fd.append("File", file);

    const modifiedHeaders = new Headers(this.defaultHeaders);
    modifiedHeaders.delete("Content-Type");
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: fd,
      headers: modifiedHeaders,
    });

    return await result.json();
  }

  async createCompany(
    companyData: Partial<CompanyApiType> & {
      firstAndMiddleName?: string;
      surname?: string;
      currency: CurrencyCodes;
    }
  ) {
    const url = new URL(this.mainUrl + "internal/company/info/create");
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(companyData),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async updateCompany(companyData: Partial<CompanyApiType>) {
    const url = new URL(this.mainUrl + "internal/company/info/update");
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(companyData),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async requestStripePaymentAccess() {
    const url = new URL(
      this.mainUrl + "internal/company/info/request-stripe-payment-access"
    );
    const result = await this.fetcher(url.toString(), {
      method: "PATCH",
      body: JSON.stringify({
        stripePaymentStatus: StripePaymentStatus.REQUESTED,
      }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async getCompaniesHouseData(crn: string) {
    const url = new URL(this.mainUrl + "public/companies-house/get");
    url.searchParams.set("q", crn);
    const result = await this.fetcher(url.toString(), {
      method: "GET",
    });
    const data = await result.json();
    return data.data;
  }

  async getSumsubRedirectData(officerId: string) {
    const url = new URL(
      this.mainUrl +
        `public/sumsub/verification-redirect-data?officerId=${officerId}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async getPublicInviteeData(
    userId: string,
    companyId: string
  ): Promise<{
    firstName: string;
    lastName: string;
    companyName: string;
    email: string;
    confirmed: boolean;
  }> {
    const queryString = stringify({
      userId,
      companyId,
    });
    const url = new URL(`${this.mainUrl}public/sign-up/data/?${queryString}`);

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async populateOfficerData() {
    const url = new URL(this.mainUrl + "internal/company/officer/list");
    const res = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return await res.json();
  }

  async updateOfficer(
    id: string,
    data: {
      email: string;
    }
  ) {
    const url = new URL(
      this.mainUrl + `internal/company/officer/${id || ""}/update`
    );
    const res = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(data),
      headers: this.defaultHeaders,
    });
    return await res.json();
  }

  async deleteOfficer(id: string) {
    const url = new URL(
      this.mainUrl + `internal/company/officer/${id || ""}/delete`
    );
    const res = await this.fetcher(url.toString(), {
      method: "DELETE",
      headers: this.defaultHeaders,
    });
    return await res.json();
  }

  async refreshOfficerData() {
    const url = new URL(this.mainUrl + "internal/company/officer/refresh");
    const res = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
    });
    return await res.json();
  }

  async addOfficersToCompany(data: Partial<CompanyOfficer>) {
    const url = new URL(
      this.mainUrl + "internal/company/officer/manual-create"
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(data),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async getSumsubAccessToken(userId: string) {
    const url = new URL(this.mainUrl + "public/sumsub/token");
    url.searchParams.set("userId", userId);
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async sendInviteToDirectorToVerify(
    directorEmail: string,
    directorId: string
  ) {
    const url = new URL(
      `${this.mainUrl}internal/company/officer/${directorId ?? ""}/invite`
    );
    const result = await this.fetcher(url.toString(), {
      method: "DELETE",
      headers: this.defaultHeaders,
      body: JSON.stringify({ email: directorEmail }),
    });
    const data = await result.json();
    return data;
  }

  async getCategories(customFilter?: Record<string, any>): Promise<any> {
    const queryString = stringify({
      ...customFilter,
    });
    const url = new URL(
      `${this.mainUrl}internal/inventory/category/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getCategory(id: string): Promise<any> {
    const url = new URL(
      `${this.mainUrl}internal/inventory/category/${id}/data`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async createCategory(data: Partial<CategoryApiType>, image?: File | null) {
    const url = new URL(this.mainUrl + "internal/inventory/category/create");
    const fd = new FormData();
    appendNestedObjectToFormData(fd, data);
    if (image) fd.append("File", image);
    const modifiedHeaders = new Headers(this.defaultHeaders);
    modifiedHeaders.delete("Content-Type");
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: modifiedHeaders,
      body: fd,
    });
    return await result.json();
  }

  async deleteCategory(data: { categoryId: string; newCategoryId?: string }) {
    const url = new URL(this.mainUrl + "internal/inventory/category/delete");
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(data),
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async updateCategory(
    data: Partial<CategoryApiType>,
    id: string,
    image?: File | null
  ) {
    const url = new URL(
      this.mainUrl + `internal/inventory/category/${id}/update`
    );
    const fd = new FormData();
    appendNestedObjectToFormData(fd, data);
    if (image) fd.append("File", image);
    const modifiedHeaders = new Headers(this.defaultHeaders);
    modifiedHeaders.delete("Content-Type");
    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      body: fd,
      headers: modifiedHeaders,
    });
    return await result.json();
  }

  /* Products */
  async createProduct(product: Partial<Product>) {
    const url = new URL(this.mainUrl + "internal/inventory/product/create");
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(product),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async attachImagesToProductVariant(
    productId: string,
    variantId: string,
    images: File[]
  ) {
    const url = new URL(
      this.mainUrl +
        `internal/inventory/product/${productId}/variant/${variantId}/attach-images`
    );
    const fd = new FormData();
    if (images.length > 0) {
      for (const f of images) {
        fd.append("Files", f);
      }
    }
    const modifiedHeaders = new Headers(this.defaultHeaders);
    modifiedHeaders.delete("Content-Type");

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: fd,
      headers: modifiedHeaders,
    });
    return await result.json();
  }

  async detachImagesFromProductVariant(
    productId: string,
    variantId: string,
    ids: string[]
  ) {
    const url = new URL(
      this.mainUrl +
        `internal/inventory/product/${productId}/variant/${variantId}/detach-images`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ ids }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async pushUpdatedProductToIntegrations(productId: string) {
    const url = new URL(
      this.mainUrl +
        `internal/inventory/product/${productId}/push-to-integrations`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async pushCreatedProductToIntegrations(productId: string) {
    const url = new URL(
      this.mainUrl +
        `internal/inventory/product/${productId}/push-created-to-integrations`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async getProducts(
    page: number,
    limit: number,
    status?: string,
    filter?: Record<string, any>,
    inventoryStatus?: string
  ) {
    try {
      const queryString = stringify({
        limit,
        status,
        page,
        ...filter,
        inventoryStatus,
      });

      const url = new URL(
        this.mainUrl + `internal/inventory/product/list?${queryString}`
      );
      const result = await this.fetcher(url.toString(), {
        method: "GET",
        headers: this.defaultHeaders,
      });
      const data = await result.json();
      return data.data;
    } catch (e: any) {
      console.warn(e);
      throw e;
    }
  }

  async getOrderGenProducts(
    page: number,
    limit: number,
    status?: string,
    category?: string,
    searchKeyword?: string,
    brands?: string,
    hasWholeSalePrice?: boolean,
    filter?: Record<string, any>,
    inventoryStatus?: string
  ) {
    try {
      const queryString = stringify({
        limit,
        status,
        category,
        searchKeyword,
        brands,
        hasWholeSalePrice,
        page,
        ...filter,
        inventoryStatus,
      });

      const url = new URL(
        this.mainUrl + `internal/inventory/product/list?${queryString}`
      );
      const result = await this.fetcher(url.toString(), {
        method: "GET",
        headers: this.defaultHeaders,
      });
      const data = await result.json();
      return data.data;
    } catch (e: any) {
      console.warn(e);
      throw e;
    }
  }

  async getProduct(id: string) {
    try {
      const url = new URL(
        `${this.mainUrl}internal/inventory/product/${id}/data`
      );
      const result = await this.fetcher(url.toString(), {
        method: "get",
        headers: this.defaultHeaders,
      });
      const data = await result.json();
      return data.data;
    } catch (e: any) {
      console.warn(e);
    }
  }

  async updateProduct(id: string, product: Partial<Product>) {
    try {
      const url = new URL(
        `${this.mainUrl}internal/inventory/product/${id}/update`
      );
      const result = await this.fetcher(url.toString(), {
        method: "PUT",
        body: JSON.stringify(product),
        headers: this.defaultHeaders,
      });

      return await result.json();
    } catch (e: any) {
      console.warn(e);
    }
  }

  async updateVariant(
    productId: string,
    variantId: string,
    variant: Partial<ProductVariantApiType>
  ) {
    try {
      const url = new URL(
        `${this.mainUrl}internal/inventory/product/${productId}/variant/${variantId}/update`
      );
      const result = await this.fetcher(url.toString(), {
        method: "PUT",
        body: JSON.stringify(variant),
        headers: this.defaultHeaders,
      });

      return await result.json();
    } catch (e: any) {
      console.warn(e);
    }
  }

  async deleteProduct(id: string) {
    try {
      const url = new URL(
        `${this.mainUrl}internal/inventory/product/${id}/delete`
      );
      const result = await this.fetcher(url.toString(), {
        method: "POST",
        headers: this.defaultHeaders,
      });
      const data = await result.json();
      return data.data;
    } catch (e: any) {
      console.warn(e);
    }
  }

  async bulkArchiveProducts(productIds: string[]) {
    try {
      const url = new URL(
        `${this.mainUrl}internal/inventory/product/bulk-archive`
      );
      const result = await this.fetcher(url.toString(), {
        method: "POST",
        body: JSON.stringify({ ids: productIds }),
        headers: this.defaultHeaders,
      });
      const data = await result.json();
      return data.data;
    } catch (e: any) {
      console.warn(e);
    }
  }

  async bulkStatusUpdateProducts(productIds: string[], status: ProductStatus) {
    try {
      const url = new URL(
        `${this.mainUrl}internal/inventory/product/bulk-status-update`
      );
      const result = await this.fetcher(url.toString(), {
        method: "POST",
        body: JSON.stringify({ ids: productIds, status }),
        headers: this.defaultHeaders,
      });
      const data = await result.json();
      return data.data;
    } catch (e: any) {
      console.warn(e);
    }
  }

  async bulkMarkVariantsAsOutOfStock(ids: string[]) {
    try {
      const url = new URL(
        `${this.mainUrl}internal/inventory/product/mark-as-out-of-stock`
      );
      const result = await this.fetcher(url.toString(), {
        method: "POST",
        body: JSON.stringify({ ids }),
        headers: this.defaultHeaders,
      });
      const data = await result.json();
      return data.data;
    } catch (e: any) {
      console.warn(e);
    }
  }

  async getProductVariants(
    id: string,
    page: number,
    limit: number,

    filter?: Record<string, any>
  ) {
    try {
      const queryString = stringify({
        limit,
        page,
        ...filter,
      });
      const url = new URL(
        `${this.mainUrl}internal/inventory/product/${id}/variant/list?${queryString}`
      );
      const result = await this.fetcher(url.toString(), {
        method: "get",
        headers: this.defaultHeaders,
      });
      const data = await result.json();
      return data.data;
    } catch (e: any) {
      console.warn(e);
    }
  }

  async updateProductOptions(id: string, formData: OptionFormValues[]) {
    try {
      const url = new URL(
        `${this.mainUrl}internal/inventory/product/${id}/update-options`
      );
      const result = await this.fetcher(url.toString(), {
        method: "PUT",
        body: JSON.stringify(formData),
        headers: this.defaultHeaders,
      });

      return await result.json();
    } catch (e: any) {
      console.warn(e);
    }
  }

  /* Brands */
  async getBrands(customFilter?: Record<string, any>): Promise<any> {
    const queryString = stringify({
      ...customFilter,
    });
    const url = new URL(
      `${this.mainUrl}internal/inventory/brand/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async createBrand(data: Partial<Brand>, image: File | null) {
    const url = new URL(this.mainUrl + "internal/inventory/brand/create");
    const fd = new FormData();
    appendNestedObjectToFormData(fd, data);
    if (image) fd.append("File", image);

    const modifiedHeaders = new Headers(this.defaultHeaders);
    modifiedHeaders.delete("Content-Type");
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: fd,
      headers: modifiedHeaders,
    });
    return await result.json();
  }

  async updateBrand(
    data: Partial<BrandApiType>,
    id: string,
    image?: File | null
  ) {
    const url = new URL(this.mainUrl + `internal/inventory/brand/${id}/update`);
    const fd = new FormData();
    appendNestedObjectToFormData(fd, data);
    if (image) fd.append("File", image);
    const modifiedHeaders = new Headers(this.defaultHeaders);
    modifiedHeaders.delete("Content-Type");
    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      body: fd,
      headers: modifiedHeaders,
    });
    return await result.json();
  }

  async deleteBrand(id: string) {
    const url = new URL(this.mainUrl + `internal/inventory/brand/${id}/delete`);
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  // PAYMENTS
  async createPayment(
    paymentData: Partial<PaymentApiType>,
    type: PaymentProviders
  ) {
    const url = new URL(this.mainUrl + "internal/payments/single/create");
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ ...paymentData, paymentProvider: type }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async createAndProcessPayment(
    paymentData: Partial<PaymentApiType>,
    type: PaymentProviders
  ) {
    const url = new URL(
      this.mainUrl + "internal/payments/single/create-and-process"
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ ...paymentData, paymentProvider: type }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async editDraftPayment(id: string, data: Partial<Payment>) {
    const url = new URL(this.mainUrl + `internal/payments/single/${id}/edit`);
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(data),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async processPayment(id: string) {
    const url = new URL(
      this.mainUrl + `internal/payments/single/${id}/process`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async updatePaymentStatus(id: string, nextStatus: PaymentStatus) {
    const url = new URL(
      this.mainUrl + `internal/payments/single/${id}/update-status`
    );
    const result = await this.fetcher(url.toString(), {
      method: "PATCH",
      body: JSON.stringify({ status: nextStatus }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async cancelPayment(id: string) {
    const url = new URL(this.mainUrl + `internal/payments/single/${id}/cancel`);
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async deletePayment(id: string) {
    const url = new URL(this.mainUrl + `internal/payments/single/${id}/delete`);
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async attachNoteToPayment(id: string, note: Partial<Note>) {
    const url = new URL(this.mainUrl + `internal/payments/single/${id}/note`);
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(note),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async editPaymentNote(id: string, noteId: string, note: Partial<Note>) {
    const url = new URL(
      this.mainUrl + `internal/payments/single/${id}/note/${noteId}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      body: JSON.stringify(note),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async deletePaymentNote(id: string, noteId: string) {
    const url = new URL(
      this.mainUrl + `internal/payments/single/${id}/note/${noteId}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "DELETE",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async sendPaymentEmail(id: string) {
    const url = new URL(
      this.mainUrl + `internal/payments/single/${id}/send-email`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async createDraftRecurringPayment(
    recurringPaymentData: Partial<RecurringPayment>
  ) {
    const url = new URL(
      this.mainUrl + `internal/payments/recurring/create-draft`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify(recurringPaymentData),
    });
    return await result.json();
  }

  async createAndProcessRecurringPayment(
    recurringPaymentData: Partial<RecurringPayment>
  ) {
    const url = new URL(
      this.mainUrl + `internal/payments/recurring/create-and-process`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify(recurringPaymentData),
    });
    return await result.json();
  }

  async getRecurringPayment(id: string) {
    const url = new URL(
      this.mainUrl + `internal/payments/recurring/${id}/data`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async getRecurringPaymentTransactions(id: string) {
    const url = new URL(
      this.mainUrl + `internal/payments/recurring/${id}/transactions/list`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async updateDraftRecurringPayment(
    id: string,
    recurringPaymentData: Partial<RecurringPayment>
  ) {
    const url = new URL(
      this.mainUrl + `internal/payments/recurring/${id}/update`
    );
    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      headers: this.defaultHeaders,
      body: JSON.stringify(recurringPaymentData),
    });
    return await result.json();
  }

  async processDraftRecurringPayment(id: string) {
    const url = new URL(
      this.mainUrl + `internal/payments/recurring/${id}/process`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async updateAndProcessRecurringPayment(
    rpData: Partial<RecurringPayment> & { initialPaymentAmount?: string },
    id: string
  ) {
    const url = new URL(
      this.mainUrl + `internal/payments/recurring/${id}/update-and-process`
    );
    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      body: JSON.stringify(rpData),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async cancelRecurringPayment(id: string) {
    const url = new URL(
      this.mainUrl + `internal/payments/recurring/${id}/cancel`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async deleteRecurringPayment(id: string) {
    const url = new URL(
      `${this.mainUrl}internal/payments/recurring/${id}/delete`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.deleted;
  }

  async getRpByInitialPayment(initialPaymentId: string) {
    const url = new URL(
      `${this.mainUrl}internal/payments/recurring/${initialPaymentId}/get-by-initial`
    );

    const result = await fetch(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async getUkHolidays() {
    const url = new URL("https://www.gov.uk/bank-holidays.json");
    const result = await this.fetcher(url.toString(), {
      method: "GET",
    });
    return await result.json();
  }

  async getPaginatedTransactions(
    page: number,
    limit?: number,
    direction?: string,
    customFilter?: Record<string, any>
  ) {
    const queryString = stringify({
      limit,
      direction,
      page,
      ...customFilter,
    });
    const url = new URL(
      `${this.mainUrl}internal/payments/transactions/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getTransactionCSV(customFilter?: Record<string, any>) {
    const queryString = stringify({
      limit: 99999,
      page: 1,
      ...customFilter,
    });
    const url = new URL(
      `${this.mainUrl}internal/payments/transactions/download?${queryString}`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return result;
  }

  async getPaginatedPayments(
    page: number,
    limit?: number,
    paymentProvider?: string,
    status?: string,
    customFilter?: Record<string, any>
  ) {
    const queryString = stringify({
      limit,
      paymentProvider,
      page,
      status,
      ...(paymentProvider === PaymentProvidersCheckout
        ? { isDoublePayment: true }
        : {}),
      ...customFilter,
    });
    const url = new URL(
      `${this.mainUrl}internal/payments/single/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getPayment(id: string): Promise<PaymentApiType> {
    const url = new URL(`${this.mainUrl}internal/payments/single/${id}`);
    const result = await this.fetcher(url.toString(), {
      method: "get",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getPaginatedPayouts(
    page: number,
    limit?: number,
    paymentProvider?: string,
    status?: string,
    customFilter?: Record<string, any>
  ) {
    const queryString = stringify({
      limit,
      paymentProvider,
      page,
      status,
      ...customFilter,
    });
    const url = new URL(
      `${this.mainUrl}internal/subscriptions/stripe/account/dashboard/payouts/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  async getPaginatedDisputes(
    page: number,
    limit?: number,
    customFilter?: Record<string, any>
  ) {
    const queryString = stringify({
      limit,
      page,
      ...customFilter,
    });
    const url = new URL(
      `${this.mainUrl}internal/subscriptions/stripe/account/dashboard/disputes/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  async useGetPayoutDetails(id: string) {
    const url = new URL(
      this.mainUrl +
        `internal/subscriptions/stripe/account/dashboard/payouts/details/${id}`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async updateUncontrolledPaymentStatus(
    paymentId: string,
    status: PaymentStatus,
    completedAt?: Date
  ) {
    const url = new URL(
      this.mainUrl + `internal/payments/single/${paymentId}/update-status`
    );
    const result = await this.fetcher(url.toString(), {
      method: "PATCH",
      body: JSON.stringify({ status, completedAt }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async attachFileToPayment(attachment: File, paymentId: string) {
    const fd = new FormData();
    fd.append("file", attachment);
    const url = new URL(
      this.mainUrl + `internal/payments/single/${paymentId}/attach-multipart`
    );
    const modifiedHeaders = new Headers(this.defaultHeaders);
    modifiedHeaders.delete("Content-Type");
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: modifiedHeaders,
      body: fd,
    });
    return await result.json();
  }

  async deleteAttachFromPayment(paymentId: string) {
    const url = new URL(
      this.mainUrl + `internal/payments/single/${paymentId}/delete-attachment`
    );
    const result = await this.fetcher(url.toString(), {
      method: "DELETE",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  /* BULK PAYMENTS */
  async createPaymentRunByCSV(file: File) {
    try {
      const url = new URL(this.mainUrl + "internal/payments/bulk/import");
      const fd = new FormData();
      fd.append("File", file);
      const modifiedHeaders = new Headers(this.defaultHeaders);
      modifiedHeaders.delete("Content-Type");
      const result = await this.fetcher(url.toString(), {
        method: "POST",
        body: fd,
        headers: modifiedHeaders,
      });
      return await result.json();
    } catch (error: any) {
      return { responseError: error };
    }
  }

  async getPaginatedBulkPayments(customFilters: Record<string, any>) {
    const queryString = stringify({
      ...customFilters,
    });
    const url = new URL(
      `${this.mainUrl}internal/payments/bulk/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getPaginatedUpcomingPayments(customFilters: Record<string, any>) {
    const queryString = stringify({
      ...customFilters,
    });
    const url = new URL(
      `${this.mainUrl}internal/payments/bulk/upcoming-payments/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getIndividualPaymentsList(
    id?: string | undefined,
    customFilter?: Record<string, any>
  ) {
    const queryString = stringify({
      ...customFilter,
    });
    const url = new URL(
      `${this.mainUrl}internal/payments/bulk/${
        id ?? ""
      }/individual-payments/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getBulkPayment(id: string) {
    const url = new URL(`${this.mainUrl}internal/payments/bulk/${id}/read`);
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getIndividualPayment(individualPaymentId: string) {
    const url = new URL(
      `${this.mainUrl}internal/payments/bulk/individual-payments/${individualPaymentId}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async createUpcomingPayment(
    individualPayment: Partial<IndividualPaymentApiType>
  ) {
    const url = new URL(
      `${this.mainUrl}internal/payments/bulk/upcoming-payments/create`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify({ individualPayment }),
    });
    return await result.json();
  }

  async createPaymentRunFromUpcomingPayments(individualPaymentsIds: string[]) {
    const url = new URL(
      `${this.mainUrl}internal/payments/bulk/upcoming-payments/payments-run/create`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify({ individualPaymentsIds }),
    });
    return await result.json();
  }

  async updateIndividualPayment(
    individualPaymentId: string,
    updatedIndividualPayment: Partial<IndividualPaymentApiType>
  ) {
    const url = new URL(
      `${this.mainUrl}internal/payments/bulk/individual-payments/${individualPaymentId}/update`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify(updatedIndividualPayment),
    });
    return await result.json();
  }

  async duplicatePaymentRun(paymentRunId: string) {
    const url = new URL(
      `${this.mainUrl}internal/payments/bulk/${paymentRunId}/duplicate`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async actualiseIndividualPayments(
    paymentRunId: string,
    individualPaymentIds: string[]
  ) {
    const url = new URL(
      `${this.mainUrl}internal/payments/bulk/${paymentRunId}/individual-payments/actualise`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ individualPayments: individualPaymentIds }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async confirmPaymentRunInitiation(
    paymentRunId: string,
    scheduledPaymentDate?: Date
  ) {
    const url = new URL(
      `${this.mainUrl}internal/payments/bulk/${paymentRunId}/initiate-payment`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({
        scheduledPaymentDate,
      }),
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async deleteIndividualPayment(individualPaymentId: string) {
    const url = new URL(
      `${this.mainUrl}internal/payments/bulk/individual-payments/${individualPaymentId}/delete`
    );
    const result = await this.fetcher(url.toString(), {
      method: "DELETE",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async getBalance(id: string) {
    const url = new URL(
      `${this.mainUrl}internal/company/bank-account/${id}/balance`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data?.value?.amount;
  }

  async editPaymentRun(paymentRunId: string, bulkPayment: BulkPaymentApiType) {
    const url = new URL(
      this.mainUrl + `internal/payments/bulk/${paymentRunId}/edit`
    );
    const result = await this.fetcher(url.toString(), {
      method: "PATCH",
      headers: this.defaultHeaders,
      body: JSON.stringify(bulkPayment),
    });
    return await result.json();
  }

  async removePaymentRun(id: string) {
    const url = new URL(this.mainUrl + `internal/payments/bulk/${id}/delete`);
    const result = await this.fetcher(url.toString(), {
      method: "DELETE",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  /* INVOICES */

  async createInvoice(invoiceData: Partial<CreateInvoiceBody>) {
    const url = new URL(this.mainUrl + "internal/payments/invoice/create");
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(invoiceData),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async createAndProcessInvoice(
    invoiceData: Partial<CreateInvoiceBody> & {
      sendEmail: boolean;
      emailRecipientName?: string;
      email?: string;
      emailCC?: string[];
    }
  ) {
    const url = new URL(
      this.mainUrl + "internal/payments/invoice/create-and-process"
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(invoiceData),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async processInvoice(
    id: string,
    sendEmail: boolean,
    emailRecipientName?: string,
    email?: string,
    emailCC?: string[]
  ) {
    const url = new URL(
      this.mainUrl + `internal/payments/invoice/${id}/process`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ sendEmail, emailRecipientName, email, emailCC }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async markAsPaidInvoice(id: string, reqBody: MarkAsPaidInvoiceBody) {
    const url = new URL(
      this.mainUrl + `internal/payments/invoice/${id}/mark-as-paid`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ ...reqBody }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async duplicateInvoice(id: string, isQuoteDuplicate: boolean) {
    const url = new URL(
      this.mainUrl +
        `internal/payments/invoice/${id}/duplicate?isQuoteDuplicate=${isQuoteDuplicate}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async getPaginatedInvoices(
    page: number,
    limit?: number,
    customFilter?: InvoicesFilters & { showDeletedQuotes?: string | null }
  ) {
    const queryString = stringify({
      limit,
      page,
      ...customFilter,
    });
    const url = new URL(
      `${this.mainUrl}internal/payments/invoice/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getInvoice(id: string): Promise<InvoiceFullData> {
    const url = new URL(`${this.mainUrl}internal/payments/invoice/${id}`);
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getInvoiceByOrderId(orderId: string) {
    try {
      const url = new URL(
        this.mainUrl + `internal/payments/invoice/${orderId}/get-by-order`
      );
      const result = await this.fetcher(url.toString(), {
        method: "GET",
        headers: this.defaultHeaders,
      });
      const data = await result.json();
      return data?.data;
    } catch (e: any) {
      console.warn(e);
    }
  }

  async updateDraftInvoice(id: string, data: Partial<CreateInvoiceBody>) {
    const url = new URL(this.mainUrl + `internal/payments/invoice/${id}/edit`);
    const result = await this.fetcher(url.toString(), {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async updateInvoiceProducts(id: string, products: OrderProductBody[]) {
    const url = new URL(
      this.mainUrl + `internal/payments/invoice/${id}/update-products`
    );
    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      body: JSON.stringify({ products }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async deleteInvoice(id: string, isQuoteDeletion?: boolean) {
    const queryString = stringify({
      isQuoteDeletion,
    });
    const url = new URL(
      this.mainUrl + `internal/payments/invoice/${id}/delete?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "DELETE",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.deleted;
  }

  async cancelInvoice(id: string) {
    const url = new URL(
      this.mainUrl + `internal/payments/invoice/${id}/cancel`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async markQuoteAsApproved(id: string) {
    const url = new URL(
      this.mainUrl + `internal/payments/invoice/${id}/approve-quote`
    );
    const result = await this.fetcher(url.toString(), {
      method: "PATCH",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async archiveInvoice(id: string, isQuoteDeletion?: boolean) {
    const queryString = stringify({
      isQuoteDeletion,
    });
    const url = new URL(
      this.mainUrl + `internal/payments/invoice/${id}/archive?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "DELETE",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  async getInvoicePdf(
    id: string,
    responseType?: "file" | "html",
    isQuote?: boolean
  ) {
    const queryString = stringify({
      responseType,
      isQuote,
    });
    const url = new URL(
      `${this.mainUrl}internal/payments/invoice/${id}/pdf?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async attachNoteToInvoice(
    invoiceId: string,
    text: string,
    noteFor: PersonEntityType[]
  ) {
    try {
      const url = new URL(
        this.mainUrl + `internal/payments/invoice/${invoiceId}/note`
      );
      const result = await this.fetcher(url.toString(), {
        method: "POST",
        body: JSON.stringify({ text, noteFor }),
        headers: this.defaultHeaders,
      });

      return await result.json();
    } catch (e: any) {
      console.warn(e);
    }
  }

  async archiveInvoiceNote(invoiceId: string, noteId: string) {
    try {
      const url = new URL(
        this.mainUrl +
          `internal/payments/invoice/${invoiceId}/note/${noteId}/delete`
      );
      const result = await this.fetcher(url.toString(), {
        method: "DELETE",
        headers: this.defaultHeaders,
      });
      const data = await result.json();
      return data;
    } catch (e: any) {
      console.warn(e);
    }
  }

  async editInvoiceNote(invoiceId: string, noteId: string, text: string) {
    try {
      const url = new URL(
        this.mainUrl +
          `internal/payments/invoice/${invoiceId}/note/${noteId}/edit`
      );
      const result = await this.fetcher(url.toString(), {
        method: "PUT",
        body: JSON.stringify({ text }),
        headers: this.defaultHeaders,
      });
      const data = await result.json();
      return data;
    } catch (e: any) {
      console.warn(e);
    }
  }

  async attachDeliveryAddressToLiveInvoice(
    invoiceId: string,
    addressId: string
  ) {
    const url = new URL(
      `${this.mainUrl}internal/payments/invoice/${invoiceId}/attach-delivery-address-to-live-invoice`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ addressId }),
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async resendEmailToRecipient(
    invoiceId: string,
    emailRecipientName?: string,
    email?: string,
    emailCC?: string[]
  ) {
    const url = new URL(
      `${this.mainUrl}internal/payments/invoice/${invoiceId}/resend-email`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ emailRecipientName, email, emailCC }),
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  /* COMMERCIAL INVOICES */

  async getPaginatedCommercialInvoices(
    page: number,
    limit?: number,
    customFilter?: InvoicesFilters
  ) {
    const queryString = stringify({
      limit,
      page,
      ...customFilter,
    });
    const url = new URL(
      `${this.mainUrl}internal/commercial-invoices/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async createCommercialInvoice(parentOrder: string) {
    const url = new URL(this.mainUrl + "internal/commercial-invoices/create");
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ parentOrder }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async getCommercialInvoice(id: string): Promise<CommercialInvoiceFullData> {
    const url = new URL(
      `${this.mainUrl}internal/commercial-invoices/${id}/data`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async deleteCommercialInvoice(ids: string[]) {
    const url = new URL(
      this.mainUrl + `internal/commercial-invoices/bulk-archive`
    );
    const result = await this.fetcher(url.toString(), {
      method: "DELETE",
      body: JSON.stringify({ ids }),
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.deleted;
  }

  async updateCommercialInvoice(
    id: string,
    data: Partial<UpdateCommercialInvoice>
  ) {
    const url = new URL(
      this.mainUrl + `internal/commercial-invoices/${id}/update`
    );
    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      body: JSON.stringify(data),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  // INTEGRATIONS

  async bulkUpdateCIFields(updatedFields: CINonMandatoryFields[]) {
    const url = new URL(
      this.mainUrl + "internal/commercial-invoices/bulk-update-fields"
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(updatedFields),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async bulkUpdateCIStatus(ids: string[], status: CommercialInvoiceStatus) {
    const url = new URL(
      this.mainUrl + "internal/commercial-invoices/bulk-update-status"
    );
    const result = await this.fetcher(url.toString(), {
      method: "PATCH",
      body: JSON.stringify({ ids, status }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async getCommercialInvoicePdf(
    id: string,
    responseType: PdfResponseFormat,
    markAsPrinted: boolean
  ) {
    const queryString = stringify({
      responseType,
      markAsPrinted,
    });
    const url = new URL(
      `${this.mainUrl}internal/commercial-invoices/${id}/pdf?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getBulkCommercialInvoicePdf(ids: string[]) {
    const url = new URL(
      `${this.mainUrl}internal/commercial-invoices/bulk-download-pdfs`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ ids }),
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async attachNoteToCommercialInvoice(
    invoiceId: string,
    text: string,
    noteFor: PersonEntityType[]
  ) {
    try {
      const url = new URL(
        this.mainUrl + `internal/commercial-invoices/${invoiceId}/create-note`
      );
      const result = await this.fetcher(url.toString(), {
        method: "POST",
        body: JSON.stringify({ text, noteFor }),
        headers: this.defaultHeaders,
      });

      return await result.json();
    } catch (e: any) {
      console.warn(e);
    }
  }

  async archiveCommercialInvoiceNote(invoiceId: string, noteId: string) {
    try {
      const url = new URL(
        this.mainUrl +
          `internal/commercial-invoices/${invoiceId}/notes/${noteId}/archive-note`
      );
      const result = await this.fetcher(url.toString(), {
        method: "GET",
        headers: this.defaultHeaders,
      });
      const data = await result.json();
      return data;
    } catch (e: any) {
      console.warn(e);
    }
  }

  async editCommercialInvoiceNote(
    invoiceId: string,
    noteId: string,
    text: string
  ) {
    try {
      const url = new URL(
        this.mainUrl +
          `internal/commercial-invoices/${invoiceId}/notes/${noteId}/edit-note`
      );
      const result = await this.fetcher(url.toString(), {
        method: "POST",
        body: JSON.stringify({ text }),
        headers: this.defaultHeaders,
      });
      const data = await result.json();
      return data;
    } catch (e: any) {
      console.warn(e);
    }
  }

  /* INTEGRATIONS */

  async getProviders(enabled: boolean = true): Promise<ProviderApiType[]> {
    const url = new URL(
      this.mainUrl + `public/providers/list?enabled=${enabled}`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getBankAccounts({
    ignorePoliciesCheck,
  }: {
    ignorePoliciesCheck?: boolean;
  } = {}): Promise<BankAccountApiType[]> {
    const queryString = stringify({
      ignorePoliciesCheck: ignorePoliciesCheck ?? false,
    });

    const url = new URL(
      this.mainUrl + `internal/company/bank-account/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async createAccount(accountData: Partial<BankAccount>) {
    const url = new URL(this.mainUrl + "internal/company/bank-account/create");
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify(accountData),
    });
    return await result.json();
  }

  async saveBankAccountAttachment(attachment: File, integrationId: string) {
    const fd = new FormData();
    fd.append("file", attachment);
    const url = new URL(
      this.mainUrl +
        `internal/company/bank-account/${integrationId}/attach-multipart`
    );
    const modifiedHeaders = new Headers(this.defaultHeaders);
    modifiedHeaders.delete("Content-Type");
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: modifiedHeaders,
      body: fd,
    });
    return await result.json();
  }

  async createAccessConsent(
    externalId: string,
    userId: string,
    companyId?: string
  ): Promise<any> {
    const url = new URL(this.mainUrl + "public/data-api/consent");
    const result = await fetch(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify({
        externalId,
        createdById: userId,
        companyId,
        createdByEntity: CreateEntities.USER,
        redirectUrl: `${window.location.origin}/redirects/ba/`,
      }),
    });
    return await result.json();
  }

  async getBankAccount(consentID: string) {
    const url = new URL(this.mainUrl + "internal/company/bank-account/read");
    url.searchParams.append("field", "consentID");
    url.searchParams.append("value", consentID);
    const result = await fetch(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async deleteBankAccount(id: string) {
    const url = new URL(
      this.mainUrl + `internal/company/bank-account/${id}/delete`
    );
    const result = await this.fetcher(url.toString(), {
      method: "DELETE",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.deleted;
  }

  async updateBankAccount(accountData: { accountName: string }, id: string) {
    const url = new URL(
      `${this.mainUrl}internal/company/bank-account/${id}/edit`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify(accountData),
    });
    return await result.json();
  }

  async resetBankConnection() {
    const url = new URL(this.mainUrl + "internal/company/reset");
    const result = await fetch(url.toString(), {
      method: "PUT",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async setDefaultBankAccount(id: string) {
    const url = new URL(
      this.mainUrl + `internal/company/bank-account/${id}/default`
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  async qiuckbookAuthUri() {
    const url = new URL(
      this.mainUrl + `internal/company/accounting/quickbook-auth`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  async xeroAuthUri() {
    const url = new URL(this.mainUrl + `internal/company/accounting/xero-auth`);

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  async getAccountingIntegrations() {
    const url = new URL(this.mainUrl + `internal/company/accounting/list`);

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  async keyloopRegister(payload: {
    name: string;
    enterpriseId: string;
    storeId: string;
  }) {
    const url = new URL(
      this.mainUrl + `internal/company/accounting/keyloop-register`
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify(payload),
    });
    const data = await result.json();
    return data;
  }

  async getIntegrationAccounts(id: string) {
    const url = new URL(
      this.mainUrl + `internal/company/accounting/${id}/accounts`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  async disconnectAccountingIntegration(id: string) {
    const url = new URL(
      this.mainUrl + `internal/company/accounting/${id ?? ""}/disconnect`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  async deleteAccountingIntegration(id: string) {
    const url = new URL(
      this.mainUrl + `internal/company/accounting/${id ?? ""}/delete`
    );
    const result = await this.fetcher(url.toString(), {
      method: "DELETE",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async editAccountingIntegration(id: string, data: any) {
    const url = new URL(
      this.mainUrl + `internal/company/accounting/${id ?? ""}/edit`
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(data),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async setXeroSelectedThemes(id: string, themes: any) {
    const url = new URL(
      this.mainUrl +
        `internal/company/accounting/${id ?? ""}/set-themes-payment`
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ themes }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async getIntegrationThemes(id: string) {
    const url = new URL(
      this.mainUrl + `internal/company/accounting/${id}/get-themes`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  async syncAccountingIntegration(id: string) {
    const url = new URL(
      this.mainUrl + `internal/company/accounting/${id ?? ""}/sync`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  async findAccountingIntegration(id: string) {
    const url = new URL(this.mainUrl + `internal/company/accounting/${id}`);

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  async validateRequiredStripeFields() {
    const url = new URL(
      this.mainUrl + `internal/subscriptions/stripe/onboarding/account/validate`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  async getOnboardingDetails() {
    const url = new URL(
      this.mainUrl + `internal/subscriptions/stripe/onboarding/account/details`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  async getMCCs() {
    const url = new URL(
      this.mainUrl + `internal/subscriptions/stripe/onboarding/mcc/list`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  async getRegions() {
    const url = new URL(this.mainUrl + `public/public-data/regions`);

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  async createStripeAccount(stripeData: any) {
    const url = new URL(
      `${this.mainUrl}internal/subscriptions/stripe/onboarding/account/create`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify(stripeData),
    });
    const data = await result.json();
    return data.data;
  }

  async updateStripeAccount(stripeData: any) {
    const url = new URL(
      `${this.mainUrl}internal/subscriptions/stripe/onboarding/account/update`
    );
    const result = await this.fetcher(url.toString(), {
      method: "PATCH",
      headers: this.defaultHeaders,
      body: JSON.stringify(stripeData),
    });
    const data = await result.json();
    return data.data;
  }
  // REFUNDS

  async refundTransaction(
    id: string,
    payeeAccountInfo: Partial<PayeeAccount>,
    refundAmount?: string
  ) {
    const url = new URL(`${this.mainUrl}internal/payments/single/${id}/refund`);
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify({
        payeeAccountInfo,
        refundAmount,
      }),
    });
    const data = await result.json();
    return data.data;
  }

  async getPaginatedRefunds(
    page: number,
    limit?: number,
    status?: string,
    customFilter?: Record<string, any>
  ) {
    const queryString = stringify({
      limit,
      status,
      page,
      ...customFilter,
    });
    const url = new URL(`${this.mainUrl}internal/refunds/list?${queryString}`);
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async cancelRefund(id: string) {
    const url = new URL(`${this.mainUrl}internal/refunds/payment/${id}/cancel`);
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async getRecurringPaymentsList(
    page: number,
    limit?: number,
    status?: RecurringPaymentStatus,
    customFilter?: Record<string, any>
  ) {
    const queryString = stringify({
      limit,
      status,
      page,
      ...customFilter,
    });
    const url = new URL(
      `${this.mainUrl}internal/payments/recurring/list?${queryString}&sort=createdAt&sortDirection=DESC`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  /** Roles */
  async getRolesList() {
    const url = new URL(`${this.mainUrl}internal/company/role/list`);
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });

    const data = await result.json();
    return data.data;
  }

  async createRole(
    roleData: Omit<Partial<Role>, "policies"> & {
      policies: Record<string, boolean>;
    }
  ) {
    const url = new URL(`${this.mainUrl}internal/company/role/create`);
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(roleData),
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async editRole(
    id: string,
    roleData: Omit<Partial<Role>, "policies"> & {
      policies: Record<string, boolean>;
    }
  ) {
    const url = new URL(`${this.mainUrl}internal/company/role/${id}/update`);
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(roleData),
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async deleteRole(originalRole: string, nextRole: string) {
    const url = new URL(this.mainUrl + `internal/company/role/delete`);
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ originalRole, nextRole }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  /**
   * Team management
   */
  async createTeamMember(userData: Partial<UserApiType>, file: File | null) {
    const url = new URL(this.mainUrl + "internal/company/team/create");
    const fd = new FormData();
    appendNestedObjectToFormData(fd, userData);
    const modifiedHeaders = new Headers(this.defaultHeaders);
    modifiedHeaders.delete("Content-Type");
    if (file) fd.append("File", file);
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: fd,
      headers: modifiedHeaders,
    });
    return await result.json();
  }

  async getTeamMembers(
    page: number,
    limit: number,
    customFilters?: Record<string, any>
  ) {
    const queryString = stringify({
      page,
      limit,
      ...customFilters,
    });
    const url = new URL(
      this.mainUrl + `internal/company/team/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const { data } = await result.json();
    return data;
  }

  async getTeamMembersByRoleID(roleId: string, searchKeyword?: string) {
    const queryString = stringify({
      searchKeyword,
    });
    const url = new URL(
      this.mainUrl +
        `internal/company/team/list-by-role/${roleId}?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getTeamMember(id: string): Promise<UserApiType> {
    const url = new URL(this.mainUrl + `internal/company/team/${id}/data`);
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async updateTeamMember(
    userId: string,
    userData: Partial<UserApiType>,
    file: File | null
  ) {
    const url = new URL(
      this.mainUrl + `internal/company/team/${userId}/update`
    );
    const fd = new FormData();
    appendNestedObjectToFormData(fd, userData);
    const modifiedHeaders = new Headers(this.defaultHeaders);
    modifiedHeaders.delete("Content-Type");
    if (file) fd.append("File", file);
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: fd,
      headers: modifiedHeaders,
    });
    return await result.json();
  }

  async bulkDeleteMembers(users: string[]) {
    const url = new URL(this.mainUrl + "internal/company/team/bulk-delete");
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ users }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async bulkSuspendMembers(users: string[]) {
    const url = new URL(this.mainUrl + "internal/company/team/bulk-suspend");
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ users }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async bulkActivateMembers(users: string[]) {
    const url = new URL(this.mainUrl + "internal/company/team/bulk-activate");
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ users }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async resendInvite(userId: string) {
    const url = new URL(
      this.mainUrl + `internal/company/team/${userId}/resend-invite`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async confirmInviteeSignUp(userId: string, companyId: string) {
    const queryString = stringify({ userId, companyId });
    const url = new URL(
      this.mainUrl + `public/sign-up/invitee-confirmation/?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  /**
   * API keys
   */
  async createApiKey(userData: Partial<ApiKey>) {
    const url = new URL(this.mainUrl + "internal/company/api-key/create");
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(userData),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async getApiKeys(
    customFilters?: Record<string, any>
  ): Promise<ApiKeyApiType[]> {
    const queryString = stringify({
      sort: "createdAt",
      sortDirection: "DESC",
      ...customFilters,
    });
    const url = new URL(
      this.mainUrl + `internal/company/api-key/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async deleteApiKey(id: string) {
    const url = new URL(this.mainUrl + `internal/company/api-key/${id}/delete`);
    const result = await this.fetcher(url.toString(), {
      method: "DELETE",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async getApiKey(id: string) {
    const url = new URL(this.mainUrl + `internal/company/api-key/${id}/data`);
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async editApiKey(id: string, data: Partial<ApiKey>) {
    const url = new URL(this.mainUrl + `internal/company/api-key/${id}/edit`);
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(data),
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async disableApiKey(id: string) {
    const url = new URL(
      this.mainUrl + `internal/company/api-key/${id}/disable`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });

    return await result.json();
  }
  /**
   * Supplier
   */

  async supplierCreate(supplier: CreateSupplierBody, file?: File | null) {
    const url = new URL(this.mainUrl + `internal/inventory/supplier/create`);
    const fd = new FormData();
    appendNestedObjectToFormData(fd, supplier);
    const modifiedHeaders = new Headers(this.defaultHeaders);
    modifiedHeaders.delete("Content-Type");
    if (file) fd.append("File", file);
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: fd,
      headers: modifiedHeaders,
    });
    return await result.json();
  }

  async suppliersList(customFilters: Record<string, any>) {
    const queryString = stringify({
      ...customFilters,
    });
    const url = new URL(
      this.mainUrl + `internal/inventory/supplier/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async supplierGet(id: string) {
    const url = new URL(
      this.mainUrl + `internal/inventory/supplier/${id}/data`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async supplierDelete(id: string) {
    const url = new URL(
      this.mainUrl + `internal/inventory/supplier/${id}/delete`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async bulkDeleteSuppliers(suppliers: string[]) {
    const url = new URL(
      this.mainUrl + `internal/inventory/supplier/bulk-delete`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ ids: suppliers }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async supplierUpdate(
    id: string,
    supplier: CreateSupplierBody,
    file?: File | null
  ) {
    const url = new URL(
      this.mainUrl + `internal/inventory/supplier/${id}/update`
    );
    const fd = new FormData();
    appendNestedObjectToFormData(fd, supplier);
    const modifiedHeaders = new Headers(this.defaultHeaders);
    modifiedHeaders.delete("Content-Type");
    if (file) fd.append("File", file);
    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      body: fd,
      headers: modifiedHeaders,
    });
    return await result.json();
  }

  async getSupplierByEmail(email: string) {
    const url = new URL(
      this.mainUrl + "internal/inventory/supplier/get-by-email"
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ email }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  /**
   * Customers
   */

  async createCustomer(customer: CustomerCreateBody, file?: File | null) {
    const url = new URL(this.mainUrl + `internal/inventory/customer/create`);
    const fd = new FormData();
    appendNestedObjectToFormData(fd, customer);
    const modifiedHeaders = new Headers(this.defaultHeaders);
    modifiedHeaders.delete("Content-Type");
    if (file) fd.append("File", file);
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: fd,
      headers: modifiedHeaders,
    });
    return await result.json();
  }

  async setDefaultDeliveryAddress(customerId: string, addressId: string) {
    const url = new URL(
      this.mainUrl +
        `internal/inventory/customer/${customerId}/default-delivery-address/${addressId}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "PATCH",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async setDefaultBillingAddress(customerId: string, addressId: string) {
    const url = new URL(
      this.mainUrl +
        `internal/inventory/customer/${customerId}/default-billing-address/${addressId}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "PATCH",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async editAddress(
    customerId: string,
    addressId: string,
    address: CustomerAddressBody
  ) {
    const url = new URL(
      this.mainUrl +
        `internal/inventory/customer/${customerId}/edit-address/${addressId}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(address),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async deleteAddress(customerId: string, address: string) {
    const url = new URL(
      this.mainUrl +
        `internal/inventory/customer/${customerId}/archive-address/${address}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "DELETE",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async listCustomers(
    page: number,
    limit: number,
    customFilters: Record<string, any>
  ) {
    const isRetail = customFilters.isWholesale === "false" ? true : undefined;
    const isWholesale = customFilters.isWholesale === "true" ? true : undefined;
    delete customFilters.isWholesale;
    const queryString = stringify({
      page,
      limit,
      ...customFilters,
      isRetail,
      isWholesale,
    });
    const url = new URL(
      this.mainUrl + `internal/inventory/customer/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const res = await result.json();
    return res.data;
  }

  async getCustomer(id: string) {
    const url = new URL(
      this.mainUrl + `internal/inventory/customer/${id}/data`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async deleteCustomer(id: string) {
    const url = new URL(
      this.mainUrl + `internal/inventory/customer/${id}/delete`
    );
    const result = await this.fetcher(url.toString(), {
      method: "DELETE",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async bulkDeleteCustomers(customers: string[]) {
    const url = new URL(
      this.mainUrl + `internal/inventory/customer/bulk-delete`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ ids: customers }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async getCustomers(
    page: number,
    limit?: number,
    customFilter?: Record<string, any>
  ) {
    try {
      const queryString = stringify({
        page,
        limit,
        ...customFilter,
      });
      const url = new URL(
        `${this.mainUrl}internal/inventory/customer/list?${queryString}`
      );
      const result = await this.fetcher(url.toString(), {
        method: "GET",
        headers: this.defaultHeaders,
      });
      const data = await result.json();
      return data.data;
    } catch (e: any) {
      console.warn(e);
    }
  }

  async updateAppAccessStatus(
    customerId: string,
    appId: string,
    appAccessStatus: CustomerExternalAppAccessStatus
  ) {
    const url = new URL(
      this.mainUrl +
        `internal/inventory/customer/${customerId}/update-app-access-status`
    );
    const result = await this.fetcher(url.toString(), {
      method: "PATCH",
      body: JSON.stringify({ appId, appAccessStatus }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async getCustomerByPhone(phone: {
    phoneCountryCode: string;
    phoneCode: string;
    number: string;
  }) {
    const url = new URL(
      this.mainUrl + "internal/inventory/customer/get-by-phone"
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({
        phoneCountryCode: phone.phoneCountryCode,
        phoneCode: phone.phoneCode,
        phoneNumber: phone.number,
      }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async getCustomerByEmail(email: string) {
    const url = new URL(
      this.mainUrl + "internal/inventory/customer/get-by-email"
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ email }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async addCustomerAddress(
    data: CustomerAddressBody & {
      addressType: "billing" | "delivery";
    },
    id?: string
  ) {
    const url = new URL(
      this.mainUrl + `internal/inventory/customer/${id ?? ""}/add-address`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(data),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async addAddressToCustomer(
    customerId: string,
    newAddress: Omit<CustomerAddress, "address"> & {
      address: Partial<Address>;
      addressType: string;
    }
  ) {
    const url = new URL(
      this.mainUrl + `internal/inventory/customer/${customerId}/add-address`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(newAddress),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async updateCustomer(
    id: string,
    customer: Partial<Customer> & { deleteFile?: boolean },
    file: File | null
  ) {
    const url = new URL(
      this.mainUrl + `internal/inventory/customer/${id}/update`
    );
    const fd = new FormData();
    appendNestedObjectToFormData(fd, customer);
    const modifiedHeaders = new Headers(this.defaultHeaders);
    modifiedHeaders.delete("Content-Type");
    if (file) fd.append("File", file);
    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      body: fd,
      headers: modifiedHeaders,
    });
    return await result.json();
  }

  async attachNoteToCustomer(customerId: string, text: string) {
    try {
      const url = new URL(
        this.mainUrl + `internal/inventory/customer/${customerId}/note`
      );
      const result = await this.fetcher(url.toString(), {
        method: "POST",
        body: JSON.stringify({ text, noteFor: [PersonEntityType.USER] }),
        headers: this.defaultHeaders,
      });

      return await result.json();
    } catch (e) {
      console.warn(e);
    }
  }

  async editCustomerNote(customerId: string, noteId: string, text: string) {
    try {
      const url = new URL(
        this.mainUrl +
          `internal/inventory/customer/${customerId}/note/${noteId}/edit`
      );
      const result = await this.fetcher(url.toString(), {
        method: "PUT",
        body: JSON.stringify({ text }),
        headers: this.defaultHeaders,
      });

      return await result.json();
    } catch (e) {
      console.warn(e);
    }
  }

  async archiveCustomerNote(customerId: string, noteId: string) {
    try {
      const url = new URL(
        this.mainUrl +
          `internal/inventory/customer/${customerId}/note/${noteId}/delete`
      );
      const result = await this.fetcher(url.toString(), {
        method: "DELETE",
        headers: this.defaultHeaders,
      });
      const data = await result.json();
      return data;
    } catch (e) {
      console.warn(e);
    }
  }

  async getCustomerTags(customFilter?: Record<string, any>): Promise<any> {
    const url = new URL(`${this.mainUrl}internal/inventory/customer/tags`);
    if (customFilter) {
      Object.entries(customFilter).forEach(([key, value]) => {
        url.searchParams.append(key, String(value));
      });
    }
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async addCustomerTag(customerId: string, tag: string): Promise<any> {
    const url = new URL(
      `${this.mainUrl}internal/inventory/customer/${customerId}/tag`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify({ tag }),
    });
    const data = await result.json();
    return data.data;
  }

  async removeCustomerTag(customerId: string, tag: string): Promise<any> {
    const url = new URL(
      `${this.mainUrl}internal/inventory/customer/${customerId}/tag`
    );
    const result = await this.fetcher(url.toString(), {
      method: "DELETE",
      headers: this.defaultHeaders,
      body: JSON.stringify({ tag }),
    });
    const data = await result.json();
    return data.data;
  }

  /**
   * Orders
   */
  async getOrders(
    page: number,
    limit?: number,
    customFilter?: Record<string, any>
  ) {
    try {
      const queryString = stringify({
        page,
        limit,
        ...customFilter,
      });
      const url = new URL(
        `${this.mainUrl}internal/inventory/order/list?${queryString}`
      );
      const result = await this.fetcher(url.toString(), {
        method: "GET",
        headers: this.defaultHeaders,
      });
      const data = await result.json();
      return data.data;
    } catch (e: any) {
      console.warn(e);
    }
  }

  async getOrderDetails(orderId: string) {
    try {
      const url = new URL(
        this.mainUrl + `internal/inventory/order/${orderId}/get`
      );
      const result = await this.fetcher(url.toString(), {
        method: "GET",
        headers: this.defaultHeaders,
      });
      const data = await result.json();
      return data;
    } catch (e: any) {
      console.warn(e);
    }
  }

  async updateOrder(orderId: string, orderData: Partial<Order>) {
    const url = new URL(
      this.mainUrl + `internal/inventory/order/${orderId}/update`
    );
    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      body: JSON.stringify(orderData),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async updateOrderApplyWholesaleShipping(
    orderId: string,
    shippingCost: string
  ) {
    const url = new URL(
      this.mainUrl +
        `internal/inventory/order/${orderId}/apply-wholesale-shipping`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ shippingCost }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async updateOrderComment(orderId: string, orderData: Partial<Order>) {
    const url = new URL(
      this.mainUrl + `internal/inventory/order/${orderId}/update-comment`
    );
    const result = await this.fetcher(url.toString(), {
      method: "PATCH",
      body: JSON.stringify(orderData),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async updateOrderProducts(
    orderId: string,
    products: OrderProductBody[],
    isRefund?: boolean,
    payeeAccountInfo?: Partial<PayeeAccount>
  ) {
    const url = new URL(
      this.mainUrl + `internal/inventory/order/${orderId}/update-products`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ products, isRefund, payeeAccountInfo }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async attachNoteToOrder(orderId: string, text: string) {
    const url = new URL(
      this.mainUrl + `internal/inventory/order/${orderId}/create-note`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ text, visibleTo: [PersonEntityType.USER] }),
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async editOrderNote(orderId: string, noteId: string, text: string) {
    const url = new URL(
      this.mainUrl +
        `internal/inventory/order/${orderId}/notes/${noteId}/edit-note`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ text, visibleTo: [PersonEntityType.USER] }),
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async archiveOrderNote(orderId: string, noteId: string) {
    const url = new URL(
      this.mainUrl +
        `internal/inventory/order/${orderId}/notes/${noteId}/archive-note`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  async bulkDeleteDraftOrder(ids: string[]) {
    const url = new URL(this.mainUrl + "internal/inventory/order/bulk-delete");
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ ids }),
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async bulkArchiveOrder(ids: string[]) {
    const url = new URL(this.mainUrl + "internal/inventory/order/bulk-archive");
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ ids }),
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async getOrdersListCSV() {
    const url = new URL(
      this.mainUrl + "internal/inventory/order/download-list"
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return result;
  }

  async createOrder(orderRequest: CreateOrderBody) {
    const url = new URL(this.mainUrl + "internal/inventory/order/create");
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(orderRequest),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async processOrder(orderId: string, processOrderBody: ProcessOrderBody) {
    const url = new URL(
      this.mainUrl + `internal/inventory/order/${orderId}/process`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ ...processOrderBody }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async createAndProcessOrder(
    orderRequest: CreateOrderBody & ProcessOrderBody
  ) {
    const url = new URL(
      this.mainUrl + "internal/inventory/order/create-and-process"
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({
        ...orderRequest,
        sendEmail: !!orderRequest.customerEmail,
      }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async manuallyUpdateStatusOrder(orderId: string, status: OrderStatus) {
    const url = new URL(
      this.mainUrl + `internal/inventory/order/${orderId}/update-status`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ status }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async cancelOrder(orderId: string) {
    const url = new URL(
      this.mainUrl + `internal/inventory/order/${orderId}/cancel`
    );
    const result = await this.fetcher(url.toString(), {
      method: "PATCH",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async refundOrder(
    orderId: string,
    products: OrderProductBody[],
    restockProducts: boolean,
    withShipping: boolean,
    payeeAccount?: string | Partial<PayeeAccount>,
    noteTxt?: string,
    customFieldsToRefundIndexes?: number[],
    sendEmail?: boolean,
    customEmail?: string,
    customerNoteTxt?: string,
    customName?: string,
    restockToLocationId?: string
  ) {
    const url = new URL(
      this.mainUrl + `internal/inventory/order/${orderId}/refund`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({
        products,
        payeeAccount,
        restockProducts,
        noteTxt,
        visibleTo: [PersonEntityType.USER],
        withShipping,
        customFieldsToRefundIndexes,
        sendEmail,
        customEmail,
        customerNoteTxt,
        customName,
        restockToLocationId,
      }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async sendReceiptToCustomer(
    orderId: string,
    name: string,
    email: string,
    type: "receipt" | "invoice" | "quote"
  ) {
    const url = new URL(
      this.mainUrl + `internal/inventory/order/${orderId}/sendReceipt`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ name, email, type }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async getOrderByPayment(paymentId: string) {
    const url = new URL(
      this.mainUrl + `internal/inventory/order/get-by-payment/${paymentId}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async changePaymentMethod(
    orderId: string,
    paymentProvider: PaymentProviders,
    completedAt?: Date,
    note?: string
  ) {
    const url = new URL(
      this.mainUrl + `internal/inventory/order/${orderId}/change-payment-method`
    );
    const result = await this.fetcher(url.toString(), {
      method: "PATCH",
      body: JSON.stringify({ paymentProvider, completedAt, note }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async getPdfReceipt(orderId: string, type: "receipt" | "invoice" | "quote") {
    const queryString = stringify({ type });
    const url = new URL(
      this.mainUrl + `internal/inventory/order/${orderId}/pdf?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async markPaymentAsPaidOrder(id: string, reqBody: MarkAsPaidInvoiceBody) {
    const url = new URL(
      this.mainUrl + `internal/inventory/order/${id}/mark-payment-as-paid`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ ...reqBody }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  /** Pick Up Points */
  async getPickUpPoints(customFilter?: Record<string, any>): Promise<any> {
    const queryString = stringify({
      ...customFilter,
    });
    const url = new URL(
      `${this.mainUrl}internal/inventory/pickup-point/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async addPickUpPoint(data: PickUpPointApiType) {
    const url = new URL(
      this.mainUrl + "internal/inventory/pickup-point/create"
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(data),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async deletePickUpPoint(id: string) {
    const url = new URL(
      this.mainUrl + `internal/inventory/pickup-point/${id}/delete`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async editPickUpPoint(
    id: string,
    data: Partial<Omit<PickupPoint, "address"> & { address: Partial<Address> }>
  ) {
    const url = new URL(
      this.mainUrl + `internal/inventory/pickup-point/${id}/update`
    );
    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      body: JSON.stringify(data),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async userByEmailExist(email: string, checkCognitoUser: boolean) {
    const url = new URL(this.mainUrl + `public/sign-up/is-email-exist `);
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ email, checkCognitoUser }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async updateNonCogntioUser(userId: string, data: Partial<UserApiType>) {
    const url = new URL(
      this.mainUrl + `public/sign-up/${userId}/update-after-coginto-signup`
    );
    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      body: JSON.stringify(data),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async sendNonCognitoVerificationEmail(userId: string) {
    const url = new URL(
      this.mainUrl +
        `public/sign-up/${userId}/send-noncoginto-verification-email`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({}),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async updateUserAttributeVerificationStatus(
    userId: string,
    name: "phoneVerified" | "emailVerified",
    value: boolean,
    isCognitoVerification?: boolean
  ) {
    const url = new URL(
      this.mainUrl + `public/sign-up/${userId}/attribite-verification`
    );
    const result = await this.fetcher(url.toString(), {
      method: "PATCH",
      body: JSON.stringify({ name, value, isCognitoVerification }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async userUpdateMFAmethod(mfaMethod: MFAMethod) {
    const url = new URL(this.mainUrl + `internal/user/mfa-method`);
    const result = await this.fetcher(url.toString(), {
      method: "PATCH",
      headers: this.defaultHeaders,
      body: JSON.stringify({ mfaMethod }),
    });
    return await result.json();
  }

  async updateOrderCustomer(orderId: string, customerId: string) {
    const url = new URL(
      this.mainUrl + `internal/inventory/order/${orderId}/update-customer`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ customerId }),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async importProductsCSV(
    overrideExisting: boolean,
    publish: boolean,
    file: File
  ) {
    try {
      const url = new URL(this.mainUrl + "internal/inventory/product/import");
      const fd = new FormData();
      fd.append("File", file);
      fd.append("overrideExisting", overrideExisting.toString());
      fd.append("pushToExternalIntegrations", publish.toString());
      const modifiedHeaders = new Headers(this.defaultHeaders);
      modifiedHeaders.delete("Content-Type");
      const result = await this.fetcher(url.toString(), {
        method: "POST",
        body: fd,
        headers: modifiedHeaders,
      });
      return await result.json();
    } catch (error: any) {
      return { responseError: error };
    }
  }

  /* External integrations (woocommerce, zapier, shopify, etc.) */
  async createIntegration(
    provider: ExternalIntegrationTypes,
    syncSettings?: Record<string, SyncDirection>,
    integrationValues: any = {}
  ) {
    const url = new URL(this.mainUrl + `internal/company/integration/create`);
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({
        provider,
        syncSettings,
        ...integrationValues,
      }),
      headers: this.defaultHeaders,
    });
    const json = await result.json();
    return json.data;
  }

  async deleteExternalIntegration(id: string) {
    const url = new URL(
      this.mainUrl + `internal/company/integration/${id}/delete`
    );

    const result = await this.fetcher(url.toString(), {
      method: "DELETE",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  async updateExternalIntegration(
    id: string,
    data: Partial<ExternalIntegration>
  ) {
    const url = new URL(
      this.mainUrl + `internal/company/integration/${id}/update`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(data),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async listIntegrations(
    type?: ExternalIntegrationTypes | ExternalIntegrationTypes[]
  ) {
    const url = new URL(
      this.mainUrl +
        `internal/company/integration/list${
          type
            ? isArray(type)
              ? `?${type.map((el) => `type[]=${el}`).join("&")}`
              : `?type=${type}`
            : ""
        }`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const json = await result.json();
    return json.data;
  }

  async getIntegration(id: string) {
    const url = new URL(
      this.mainUrl + `internal/company/integration/${id}/data`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const json = await result.json();
    return json.data;
  }

  async downloadIntegrationPlugin(id: string) {
    const url = new URL(
      this.mainUrl + `internal/company/integration/${id}/plugin`
    );
    const response = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });

    // Get the filename from the 'Content-Disposition' header if available
    const contentDisposition = response.headers.get("content-disposition");
    let fileName = "plugin.zip"; // Default filename
    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (fileNameMatch?.[1]) {
        fileName = fileNameMatch[1];
      }
    }

    // Get the response as a blob (binary data)
    return { fileName, blob: await response.blob() };
  }

  async resyncStoreCurrency(storeId: string) {
    const url = new URL(
      this.mainUrl + `internal/company/integration/${storeId}/resync-currency`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getProductsCSV(
    customFilter?: Record<string, any>,
    sort = { field: "createdAt", direction: "DESC" },
    inventoryStatus?: string
  ) {
    const queryString = stringify({
      limit: 99999,
      page: 1,
      ...customFilter,
      sort: sort?.field,
      sortDirection: sort?.direction,
      inventoryStatus,
    });
    const url = new URL(
      `${this.mainUrl}internal/inventory/product/export?${queryString}`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return result;
  }

  /**
   * Reminders
   */
  async getRemindersList(customFilter?: Record<string, any>) {
    const queryString = stringify({
      ...customFilter,
    });
    const url = new URL(
      `${this.mainUrl}internal/company/reminder/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getReminderData(reminderId: string) {
    const url = new URL(
      `${this.mainUrl}internal/company/reminder/${reminderId}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async createReminder(reminderData: Partial<Reminder>) {
    const url = new URL(this.mainUrl + "internal/company/reminder/create");
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(reminderData),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async updateReminder(id: string, reminderBody: Partial<Reminder>) {
    const url = new URL(
      this.mainUrl + `internal/company/reminder/${id}/update`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(reminderBody),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async deleteReminder(id: string) {
    const url = new URL(
      this.mainUrl + `internal/company/reminder/${id}/delete`
    );
    const res = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
    });
    return await res.json();
  }

  async sendTestReminder(
    type: ReminderParentEntityType,
    email: string,
    subject?: string,
    message?: string
  ) {
    const url = new URL(
      this.mainUrl + `internal/company/reminder/send-test-reminder`
    );
    const res = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ type, email, subject, message }),
      headers: this.defaultHeaders,
    });
    return await res.json();
  }

  /**
   * Shipengine (Carriers)
   */

  async createCarrier(reqBody: any) {
    const url = new URL(
      this.mainUrl + "internal/company/shipping-carrier/create"
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(reqBody),
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getCarriersList(): Promise<ShippingCarrierApiType[]> {
    const url = new URL(
      this.mainUrl + "internal/company/shipping-carrier/list"
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getCarrier(carrierId: string) {
    const url = new URL(
      this.mainUrl + `internal/company/shipping-carrier/${carrierId}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getCarrierServiceCodes(carrierId: string) {
    const url = new URL(
      this.mainUrl +
        `internal/company/shipping-carrier/${carrierId}/service-codes`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async updateCarrier(id: string, reqBody: any) {
    const url = new URL(
      this.mainUrl + `internal/company/shipping-carrier/${id}/edit`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(reqBody),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async disconnectCarrier(carrierId: string) {
    const url = new URL(
      this.mainUrl + `internal/company/shipping-carrier/${carrierId}/disconnect`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getCarrierPackages(carrierId: string) {
    const url = new URL(
      this.mainUrl + `internal/company/shipping-carrier/${carrierId}/packages`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getCarrierServices(carrierId: string) {
    const url = new URL(
      this.mainUrl +
        `internal/company/shipping-carrier/${carrierId}/service-codes`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  /**
   * Shipengine (Locations)
   */

  async getLocationsList(
    page: number,
    limit: number,
    filters: LocationsFilters
  ): Promise<PaginatedResponse<LocationApiType>> {
    const queryString = stringify({
      page,
      limit,
      ...filters,
    });
    const url = new URL(
      this.mainUrl + `internal/inventory/location/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getLocation(locationId: string): Promise<
    Omit<LocationApiType, "address"> & {
      address?: Address;
    }
  > {
    const url = new URL(
      this.mainUrl + `internal/inventory/location/${locationId}/data`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getLocationAssociationsCount(locationId: string) {
    const url = new URL(
      this.mainUrl +
        `internal/inventory/location/${locationId}/associations-count`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async createLocation(
    reqBody: Partial<LocationApiType & { address?: Address }>
  ) {
    const url = new URL(this.mainUrl + "internal/inventory/location/create");
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(reqBody),
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  async updateLocation(
    locationId: string,
    reqBody: Partial<
      LocationApiType & {
        address?: Address;
        connectedIntegrations?: SalesChannel[];
      }
    >
  ) {
    const url = new URL(
      this.mainUrl + `internal/inventory/location/${locationId}/update`
    );
    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      body: JSON.stringify(reqBody),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async deleteLocation(locationId: string, reassignedLocationId?: string) {
    const url = new URL(
      this.mainUrl + `internal/inventory/location/${locationId}/delete`
    );
    const result = await this.fetcher(url.toString(), {
      method: "DELETE",
      body: JSON.stringify({ reassignedLocationId }),
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  /**
   * Shipengine (Shipments)
   */

  async getCarriersRatesFromDetails(
    orderId: string,
    locationId: string,
    shipDate: Date,
    newPackage?: ShipEnginePackage
  ) {
    const url = new URL(
      `${this.mainUrl}internal/inventory/shipment/carrier-rates`
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({
        orderId,
        locationId,
        shipDate,
        ...(newPackage ? { packages: [newPackage] } : {}),
      }),
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async createLabelFromRateId(
    orderId: string,
    rateId: string,
    locationId: string,
    carrierId: string
  ) {
    const url = new URL(
      `${this.mainUrl}internal/inventory/shipment/create-label-from-rate`
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ orderId, rateId, locationId, carrierId }),
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async createLabelFromOrder(
    orderId: string,
    rateDetails: { carrierId: string; serviceCode: string[] }
  ) {
    const url = new URL(
      `${this.mainUrl}internal/inventory/shipment/create-label-from-order`
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ orderId, rateDetails }),
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async getShipmentsByOrderId(orderId: string) {
    const url = new URL(
      `${this.mainUrl}internal/inventory/shipment/find-by-order-id/${orderId}`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async trackShipment(shipmentId: string) {
    const url = new URL(
      `${this.mainUrl}internal/inventory/shipment/${shipmentId}/track`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async createReturnLabel(shipmentId: string) {
    const url = new URL(
      `${this.mainUrl}internal/inventory/shipment/${shipmentId}/create-return-label`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async assignPickerForOrder(pickerId: string, orderIds: string[]) {
    const url = new URL(
      `${this.mainUrl}internal/inventory/order/assign-picker`
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ pickerId, orderIds }),
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async pickProductsForOrder(orderId: string, orderProductIds: string[]) {
    const url = new URL(
      `${this.mainUrl}internal/inventory/order/${orderId}/pick-products`
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ productIds: orderProductIds }),
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async generateManifest(shipmentIds: string[]) {
    const url = new URL(
      `${this.mainUrl}internal/inventory/shipment/create-manifest`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ shipmentIds }),
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async getManifests(filters?: ManifestFilters) {
    const queryString = stringify({
      ...filters,
    });
    const url = new URL(
      `${this.mainUrl}internal/inventory/shipment/list-manifests?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async sentReturnLabel(shipmentId: string, email: string) {
    const url = new URL(
      `${this.mainUrl}internal/inventory/shipment/${shipmentId}/send-label`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ email }),
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  // Subscriptions

  async getSubscriptionsData() {
    const url = new URL(
      `${this.mainUrl}internal/subscriptions/stripe/available_subscriptions`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async getB2bSubscriptionsData() {
    const url = new URL(
      `${this.mainUrl}internal/subscriptions/b2b/available_subscriptions`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async getCurrentSubscriptions() {
    const url = new URL(
      `${this.mainUrl}internal/subscriptions/stripe/my_subscriptions`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async getSubscriptionPaymentMethod() {
    const url = new URL(
      `${this.mainUrl}internal/subscriptions/stripe/get_my_payment_methods`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async getBillingHistory(limit: number, customFilters: Record<string, any>) {
    const queryString = stringify({
      limit,
      ...customFilters,
    });

    const url = new URL(
      `${this.mainUrl}internal/subscriptions/stripe/get_customer_billing_history?${queryString}`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async getSubscriptionAddsOn() {
    const url = new URL(
      `${this.mainUrl}internal/subscriptions/stripe/available_subscription_extension_packs`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async getSubscriptionStats(isSalesAgentRoleId?: string) {
    const url = new URL(
      `${this.mainUrl}internal/subscriptions/stripe/subscription_stats${
        isSalesAgentRoleId ? `?roleId=${isSalesAgentRoleId}` : ""
      }`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async createSubscription(
    data: CreateStripeSubscriptionDTO,
    trackdeskConfig: TrackDeskConfig | null
  ) {
    const url = new URL(
      `${this.mainUrl}internal/subscriptions/stripe/create_subscription`
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({ ...data, ...(trackdeskConfig ?? {}) }),
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async updateSubscriptionPaymentMethod(data: {
    stripe_payment_method_id: string;
  }) {
    const url = new URL(
      `${this.mainUrl}internal/subscriptions/stripe/update_customer_payment_method`
    );

    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      body: JSON.stringify(data),
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async updateOrDowngradeSubscription(data: { stripe_price_id?: string }) {
    const url = new URL(
      `${this.mainUrl}internal/subscriptions/stripe/upgrade_or_downgrade_subscription`
    );

    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      body: JSON.stringify(data),
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async cancelSubscription() {
    const url = new URL(
      `${this.mainUrl}internal/subscriptions/stripe/cancel_subscription`
    );

    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async purchaseExtension(data: {
    extensionPackId?: string;
    paymentOption?: PaymentToolOptions;
  }) {
    const url = new URL(
      `${this.mainUrl}internal/subscriptions/stripe/purchase_subscription_extension_pack`
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(data),
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async purchaseSaleSeatsExtension(data: {
    extensionPackId?: string;
    addonsCount?: number | string;
    paymentOption?: PaymentToolOptions;
  }) {
    const url = new URL(
      `${this.mainUrl}internal/subscriptions/stripe/user_addon/create`
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(data),
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async resumeSubscription() {
    const url = new URL(
      `${this.mainUrl}internal/subscriptions/stripe/resume_subscription`
    );

    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async retryPayment() {
    const url = new URL(
      `${this.mainUrl}internal/subscriptions/stripe/retry_payment`
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async generateStripeInvoice(id: string) {
    const url = new URL(
      `${this.mainUrl}internal/subscriptions/stripe/generate_invoice/${id}`
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",

      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async getPromotionCode(id: string) {
    const url = new URL(
      `${this.mainUrl}internal/subscriptions/stripe/promotion_code/${id}`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  async getSalesAgentsList(query?: CreateStripeUserAddonQuery) {
    const queryString = stringify({
      ...query,
    });

    const url = new URL(
      `${this.mainUrl}internal/subscriptions/stripe/user_addon/list?${queryString}`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const response = await result.json();

    return response;
  }

  async deleteSalsesAgent(packId: string) {
    const url = new URL(
      `${this.mainUrl}internal/subscriptions/stripe/user_addon/remove`
    );

    const result = await this.fetcher(url.toString(), {
      method: "DELETE",
      body: JSON.stringify({
        addonsCount: 1,
        extensionPackId: packId,
      }),
      headers: this.defaultHeaders,
    });

    return await result.json();
  }

  // purchase orders

  async getPurchaseOrdersList(
    page: number,
    limit: number,
    customFilters: Record<string, any>
  ) {
    const queryString = stringify({
      page,
      limit,
      ...customFilters,
    });
    const url = new URL(
      this.mainUrl + `internal/inventory/inventory-order/list?${queryString}`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const res = await result.json();
    return res.data;
  }

  async createPurchaseOrder(data: CreateInventoryOrderBody) {
    const url = new URL(
      this.mainUrl + "internal/inventory/inventory-order/create"
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify(data),
    });
    return await result.json();
  }

  async updatePurchaseOrder(data: UpdateInventoryOrderBody, id: string) {
    const url = new URL(
      this.mainUrl + `internal/inventory/inventory-order/${id}/update`
    );

    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      headers: this.defaultHeaders,
      body: JSON.stringify(data),
    });
    return await result.json();
  }

  async updatePoProducts(data: InventoryOrderProductBody[], id: string) {
    const url = new URL(
      this.mainUrl + `internal/inventory/inventory-order/${id}/update-products`
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify(data),
    });
    return await result.json();
  }

  async acceptPoProducts(data: InventoryOrderProductBody[], id: string) {
    const url = new URL(
      this.mainUrl + `internal/inventory/inventory-order/${id}/accept`
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify(data),
    });
    return await result.json();
  }

  async processPo(id: string) {
    const url = new URL(
      this.mainUrl + `internal/inventory/inventory-order/${id}/process`
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async getPurchaseOrder(id: string) {
    const url = new URL(
      this.mainUrl + `internal/inventory/inventory-order/${id}/data`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async deletePo(id: string) {
    const url = new URL(
      this.mainUrl + `internal/inventory/inventory-order/${id}/delete`
    );

    const result = await this.fetcher(url.toString(), {
      method: "DELETE",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async addPoNote(id: string, noteData: NoteBody) {
    const url = new URL(
      this.mainUrl + `internal/inventory/inventory-order/${id}/create-note`
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify(noteData),
    });
    return await result.json();
  }

  async updatePoNote(id: string, noteData: NoteBody) {
    const url = new URL(
      this.mainUrl + `internal/inventory/inventory-order/${id}/edit-note`
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify(noteData),
    });
    return await result.json();
  }

  async deletePoNote(id: string) {
    const url = new URL(
      this.mainUrl + `internal/inventory/inventory-order/${id}/archive-note`
    );

    const result = await this.fetcher(url.toString(), {
      method: "DELETE",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async getPoPdf(id: string) {
    const url = new URL(
      this.mainUrl + `internal/inventory/inventory-order/${id}/pdf`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });

    const data = await result.json();
    return data.data;
  }

  async sendPoEmail(id: string, email?: string, emailCC: string[] = []) {
    const url = new URL(
      this.mainUrl + `internal/inventory/inventory-order/${id}/send-email`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify({ email, emailCC }),
    });
    return await result.json();
  }

  async reconsentBankAccount(bankAccountId: string) {
    const url = new URL(
      `${this.mainUrl}internal/company/bank-account/${bankAccountId}/reconsent`
    );
    const result = await fetch(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  async fullReconsentBankAccount(bankAccountId: string) {
    const url = new URL(
      `${this.mainUrl}internal/company/bank-account/${bankAccountId}/full-reconsent`
    );
    const user = await this.getUser();
    const company = await this.getCompany();
    const result = await fetch(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify({
        redirectUrl:
          typeof window !== `undefined` && window.innerWidth < 900
            ? `${window.location.href}mobile/`
            : `${window.location.href}status/`,

        userId: user._id,
        companyId: company._id,
      }),
    });

    const data = await result.json();
    return data.data;
  }

  async getBankAccountDataById(bankAccountId: string) {
    const url = new URL(
      `${this.mainUrl}internal/company/bank-account/${bankAccountId}/data`
    );
    const result = await fetch(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async duplicateOrder(orderId: string) {
    const url = new URL(
      `${this.mainUrl}internal/inventory/order/${orderId}/duplicate`
    );
    const result = await fetch(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async duplicateInventoryOrder(orderId: string) {
    const url = new URL(
      `${this.mainUrl}internal/inventory/inventory-order/${orderId}/duplicate`
    );
    const result = await fetch(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  /**
   * Partner
   */

  async getMerchantsList(
    page: number,
    limit?: number,
    customFilter?: Record<string, any>
  ) {
    const queryString = stringify({
      page,
      limit,
      // status,
      ...customFilter,
    });
    const url = new URL(
      `${this.mainUrl}internal/partner/managed-companies/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getMerchant(id: string) {
    const url = new URL(
      `${this.mainUrl}internal/partner/managed-companies/${id}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async createMerchant(
    merchantData: Omit<Partial<Partner_CreatedCompany>, "address">,
    bankAccAttachment?: File
  ) {
    const fd = new FormData();
    if (bankAccAttachment) fd.append("bankStatement", bankAccAttachment);
    fd.append("", JSON.stringify(merchantData));
    appendNestedObjectToFormData(fd, merchantData);
    const modifiedHeaders = new Headers(this.defaultHeaders);
    modifiedHeaders.delete("Content-Type");
    const url = new URL(
      this.mainUrl + "internal/partner/managed-companies/create"
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: fd,
      headers: modifiedHeaders,
    });
    return await result.json();
  }

  async createSandboxMerchant() {
    const url = new URL(
      this.mainUrl + "internal/partner/managed-companies/create-sandbox"
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",

      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async updateMerchant(merchantData: EditCompanyDetailsFormValues, id: string) {
    const url = new URL(
      this.mainUrl + `internal/partner/managed-companies/${id}/edit`
    );

    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      body: JSON.stringify(merchantData),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async createMerchantBankAccount(
    data: MerchantBankFormValues,
    id: string,
    bankAccAttachment?: File
  ) {
    const fd = new FormData();
    if (bankAccAttachment) fd.append("bankStatement", bankAccAttachment);
    fd.append("", JSON.stringify(data));
    appendNestedObjectToFormData(fd, data);
    const modifiedHeaders = new Headers(this.defaultHeaders);
    modifiedHeaders.delete("Content-Type");
    const url = new URL(
      this.mainUrl +
        `internal/partner/managed-companies/${id}/bank-accounts/create`
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: fd,
      headers: modifiedHeaders,
    });
    return await result.json();
  }

  async updateMerchantBankAccount(
    merchantId: string,
    accountId: string,
    data: { name: string }
  ) {
    const url = new URL(
      this.mainUrl +
        `internal/partner/managed-companies/${merchantId}/bank-accounts/${accountId}/edit-name`
    );

    const result = await this.fetcher(url.toString(), {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async deleteMerchantBankAccount(merchantId: string, accountId: string) {
    const url = new URL(
      this.mainUrl +
        `internal/partner/managed-companies/${merchantId}/bank-accounts/${accountId}/delete`
    );

    const result = await this.fetcher(url.toString(), {
      method: "DELETE",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async setAsDefaultBa(merchantId: string, accountId: string) {
    const url = new URL(
      this.mainUrl +
        `internal/partner/managed-companies/${merchantId}/bank-accounts/${accountId}/set-default`
    );

    const result = await this.fetcher(url.toString(), {
      method: "PATCH",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async createMerchantIntegration(
    data: Partner_CreateManagedCompanyIntegration,
    id: string
  ) {
    const url = new URL(
      this.mainUrl +
        `internal/partner/managed-companies/${id}/integrations/create`
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify(data),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async updateMerchantIntegration(
    merchantId: string,
    integrationId: string,
    data: Partner_CreateManagedCompanyIntegration
  ) {
    const url = new URL(
      this.mainUrl +
        `internal/partner/managed-companies/${merchantId}/integrations/${integrationId}/edit`
    );

    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      body: JSON.stringify(data),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async activateOrResendInviteToSilentUser(
    managedCompanyID: string,
    userId: string
  ) {
    const url = new URL(
      this.mainUrl +
        `internal/partner/managed-companies/${managedCompanyID}/users/${userId}/activate`
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async deleteMerchantIntegration(merchantId: string, integrationId: string) {
    const url = new URL(
      this.mainUrl +
        `internal/partner/managed-companies/${merchantId}/integrations/${integrationId}/delete`
    );

    const result = await this.fetcher(url.toString(), {
      method: "DELETE",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async getMerchantIntegration(merchantId: string, integrationId: string) {
    const url = new URL(
      `${this.mainUrl}internal/partner/managed-companies/${merchantId}/integrations/${integrationId}/data`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getMerchantIntegrationsList(
    id: string,
    page: number,
    limit?: number,
    customFilter?: Record<string, any>
  ) {
    const queryString = stringify({
      page,
      limit,
      ...customFilter,
    });
    const url = new URL(
      `${this.mainUrl}internal/partner/managed-companies/${id}/integrations/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getMerchantTeamMembers(
    companyId: string,
    page: number,
    limit?: number,
    customFilter?: Record<string, any>
  ) {
    const queryString = stringify({
      page,
      limit,
      // status,
      ...customFilter,
    });
    const url = new URL(
      `${this.mainUrl}internal/partner/managed-companies/${companyId}/users?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async disableMerchant(merchantId: string) {
    const url = new URL(
      this.mainUrl +
        `internal/partner/managed-companies/${merchantId}/toggle-access`
    );

    const result = await this.fetcher(url.toString(), {
      method: "PATCH",
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async getMerchantTransaction(id: string): Promise<PaymentApiType> {
    const url = new URL(
      `${this.mainUrl}internal/partner/transactions/${id}/data`
    );
    const result = await this.fetcher(url.toString(), {
      method: "get",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getMerchantPaginatedTransactions(
    page: number,
    limit?: number,
    direction?: string,
    customFilter?: Record<string, any>
  ) {
    const queryString = stringify({
      limit,
      direction,
      page,
      ...customFilter,
    });
    const url = new URL(
      `${this.mainUrl}internal/partner/transactions/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getSingleMerchantPaginatedTransactions(
    companyId: string,
    page: number,
    limit?: number,
    direction?: string,
    customFilter?: Record<string, any>
  ) {
    const queryString = stringify({
      limit,
      direction,
      page,
      ...customFilter,
    });
    const url = new URL(
      `${this.mainUrl}internal/partner/managed-companies/${companyId}/transactions/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getMerchantsStats(customFilter?: Record<string, any>) {
    const queryString = stringify({
      ...customFilter,
    });
    const url = new URL(
      `${this.mainUrl}internal/partner/statistics/stats?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getMerchantsTopBottom(customFilter?: Record<string, any>) {
    const queryString = stringify({
      ...customFilter,
    });
    const url = new URL(
      `${this.mainUrl}internal/partner/statistics/top-bottom?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getMerchantsTransactionsBySize(customFilter?: Record<string, any>) {
    const queryString = stringify({
      ...customFilter,
    });
    const url = new URL(
      `${this.mainUrl}internal/partner/statistics/transactions-by-size?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async updateCompanySettings(reqBody: Partial<CompanySettings>) {
    const url = new URL(this.mainUrl + "internal/company/settings");
    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      body: JSON.stringify(reqBody),
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getCompanySettings() {
    const url = new URL(this.mainUrl + "internal/company/settings");
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getAllMerchantsTransactionCSV(customFilter?: Record<string, any>) {
    const queryString = stringify({
      limit: 99999,
      page: 1,
      ...customFilter,
    });
    const url = new URL(
      `${this.mainUrl}internal/partner/transactions/export?${queryString}`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return result;
  }

  async getSingleMerchantTransactionCSV(
    merchantId: string,
    customFilter?: Record<string, any>
  ) {
    const queryString = stringify({
      limit: 99999,
      page: 1,
      ...customFilter,
    });
    const url = new URL(
      `${this.mainUrl}internal/partner/managed-companies/${merchantId}/transactions/export?${queryString}`
    );

    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return result;
  }

  async getHistory(parentEntityId: string, limit: number) {
    const queryString = stringify({
      parentEntity: parentEntityId,
      page: 1,
      limit,
    });
    const url = new URL(this.mainUrl + `internal/history/?${queryString}`);
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  /* Currency Settings */
  async updateCurrencySettings(reqBody: Partial<CurrencySettings>) {
    const url = new URL(
      this.mainUrl + "internal/company/settings/currencies/update"
    );
    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      body: JSON.stringify(reqBody),
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async toggleCurrency(currencyCode: CurrencyCodes) {
    const url = new URL(
      this.mainUrl +
        `internal/company/settings/currencies/${currencyCode}/toggle`
    );
    const result = await this.fetcher(url.toString(), {
      method: "PATCH",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async deleteCurrency(currencyCode: CurrencyCodes) {
    const url = new URL(
      this.mainUrl +
        `internal/company/settings/currencies/${currencyCode}/delete`
    );
    const result = await this.fetcher(url.toString(), {
      method: "DELETE",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async exportPricingByCurrencyToCsv(currencyCode: CurrencyCodes) {
    const url = new URL(
      this.mainUrl +
        `internal/inventory/product/export-pricing/${currencyCode}/`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    return result;
  }

  async importPricingByCurrencyFromCsv(
    currencyCode: CurrencyCodes,
    file: File
  ) {
    const url = new URL(
      this.mainUrl +
        `internal/inventory/product/import-pricing/${currencyCode}/`
    );
    const fd = new FormData();
    file && fd.append("File", file);

    const modifiedHeaders = new Headers(this.defaultHeaders);
    modifiedHeaders.delete("Content-Type");
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: fd,
      headers: modifiedHeaders,
    });

    return await result.json();
  }

  async getTotalEarningChartData(
    scope: string,
    customFilter?: Record<string, any>
  ) {
    const queryString = stringify({
      scope,
      ...customFilter,
    });
    const url = new URL(
      this.mainUrl +
        `internal/subscriptions/stripe/account/dashboard/total-earnings-chart?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getAccountBalance() {
    const url = new URL(
      this.mainUrl + `internal/subscriptions/stripe/account/dashboard/balance`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  /* Segments */
  async createSegment(
    segmentData: CreateSegment & { currency?: CurrencyCodes },
    csvImportFile: File | null
  ) {
    try {
      const url = new URL(this.mainUrl + "internal/inventory/segment/create");
      const fd = new FormData();
      const locations = segmentData.locations;
      locations?.forEach((l) => {
        fd.append("locations[]", l);
      });
      delete segmentData.locations;
      appendNestedObjectToFormData(fd, segmentData);

      if (csvImportFile) fd.append("file", csvImportFile);
      const modifiedHeaders = new Headers(this.defaultHeaders);
      modifiedHeaders.delete("Content-Type");
      const result = await this.fetcher(url.toString(), {
        method: "POST",
        body: fd,
        headers: modifiedHeaders,
      });
      return await result.json();
    } catch (error: any) {
      return { responseError: error };
    }
  }

  async getSegmentsList(filters: any) {
    const queryString = stringify({
      ...filters,
    });
    const url = new URL(
      this.mainUrl + `internal/inventory/segment/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async updateSegment(segmentId: string, segmentData: UpdateSegment) {
    const url = new URL(
      this.mainUrl + `internal/inventory/segment/${segmentId}/update`
    );
    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      body: JSON.stringify(segmentData),
      headers: this.defaultHeaders,
    });
    return await result.json();
  }

  async bulkDeleteSegments(
    ids: string[],
    type: SegmentType,
    reassignedSegmentId?: string
  ) {
    const url = new URL(
      this.mainUrl + `internal/inventory/segment/bulk-delete`
    );
    const res = await this.fetcher(url.toString(), {
      method: "DELETE",
      body: JSON.stringify({ ids, type, reassignedSegmentId }),
      headers: this.defaultHeaders,
    });
    return await res.json();
  }

  async importPricingForSegmentFromCsv(
    segmentId: string,
    currency: CurrencyCodesType,
    file: File
  ) {
    try {
      const url = new URL(
        this.mainUrl + "internal/inventory/product/import-pricing-for-segment"
      );
      const fd = new FormData();
      fd.append("segmentId", segmentId);
      fd.append("currency", currency);
      fd.append("File", file);

      const modifiedHeaders = new Headers(this.defaultHeaders);
      modifiedHeaders.delete("Content-Type");
      const result = await this.fetcher(url.toString(), {
        method: "POST",
        body: fd,
        headers: modifiedHeaders,
      });
      return await result.json();
    } catch (error: any) {
      return { responseError: error };
    }
  }

  async exportCsvPricingForSegment(
    segmentId: string,
    currencyCode: CurrencyCodes
  ) {
    const queryString = stringify({
      currency: currencyCode,
    });
    const url = new URL(
      this.mainUrl +
        `internal/inventory/product/${segmentId}/export-pricing-for-segment/?${queryString}`
    );
    return await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
  }

  // Stripe checkout
  async getStripeCheckout(
    priceId: string,
    trackdeskConfig: TrackDeskConfig | null
  ) {
    const url = new URL(
      this.mainUrl +
        "internal/subscriptions/stripe/create_subscription_checkout"
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      body: JSON.stringify({
        priceId,
        ...(trackdeskConfig ?? {}),
      }),
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  async getStripeUpdatePaymentMethodCheckout() {
    const url = new URL(
      this.mainUrl +
        "internal/subscriptions/stripe/update_customer_payment_method"
    );
    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      body: JSON.stringify({}),
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  async stripePaymentRefund(paymentId: string) {
    const url = new URL(
      this.mainUrl +
        `internal/subscriptions/stripe/payments/refund/${paymentId}`
    );

    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data;
  }

  /* Integrations - B2B Apps */
  async getExternalAppList() {
    const url = new URL(this.mainUrl + `internal/company/app/list`);
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getExternalApp(id: string) {
    const url = new URL(this.mainUrl + `internal/company/app/${id}/data`);
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async updateExternalApp(id: string, data: Partial<ExternalAppApiType>) {
    const url = new URL(this.mainUrl + `internal/company/app/${id}/update`);
    const result = await this.fetcher(url.toString(), {
      method: "PUT",
      headers: this.defaultHeaders,
      body: JSON.stringify(data),
    });
    return await result.json();
  }

  async setupWholesaleTagsForExternalApp(
    id: string,
    data: {
      wholesaleOrderTags: ExternalAppApiType["wholesaleOrderTags"];
      wholesaleCustomerTags: ExternalAppApiType["wholesaleCustomerTags"];
    }
  ) {
    const url = new URL(
      this.mainUrl + `internal/company/app/${id}/setup-wholesale-tags`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify({
        wholesaleOrderTags: data.wholesaleOrderTags,
        wholesaleCustomerTags: data.wholesaleCustomerTags,
      }),
    });
    return await result.json();
  }

  async resyncAppCurrency(appId: string) {
    const url = new URL(
      this.mainUrl + `internal/company/app/${appId}/resync-currency`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async deleteExternalApp(id: string) {
    const url = new URL(this.mainUrl + `internal/company/app/${id}/delete`);
    const res = await this.fetcher(url.toString(), {
      method: "DELETE",
      headers: this.defaultHeaders,
    });
    return await res.json();
  }

  /* Sales channels */
  async getSalesChannelsList(filters: any) {
    const queryString = stringify({
      ...filters,
    });
    const url = new URL(
      this.mainUrl + `internal/company/sales-channel/list?${queryString}`
    );
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async verifyAppConnectionOptions(
    hmacData: string,
    hmac: string,
    jwtToken: string
  ) {
    const url = new URL(
      this.mainUrl + `public/app/verify-app-connection-options`
    );
    const response = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify({ hmacData, hmac, jwtToken }),
    });
    const result = await response.json();
    return result.data;
  }

  async appSignUp(token: string, email: string, password: string) {
    const url = new URL(this.mainUrl + `apps/company/create`);
    const response = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify({ token, email, password }),
    });
    const result = await response.json();
    return result;
  }

  async appSignInAndAttach(token: string, email: string, password: string) {
    const url = new URL(this.mainUrl + `apps/company/sign-in-and-attach`);
    const response = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify({ token, email, password }),
    });
    const result = await response.json();
    return result.data;
  }

  async getPaymentsFees() {
    const url = new URL(
      this.mainUrl + `internal/subscriptions/stripe/onboarding/fees/list`
    );
    const response = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const result = await response.json();
    return result;
  }

  async getLoginEvents() {
    const url = new URL(this.mainUrl + "internal/user/getLastLoginEvents");
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async getOrderTags(customFilter?: Record<string, any>): Promise<any> {
    const url = new URL(`${this.mainUrl}internal/inventory/order/tags`);
    if (customFilter) {
      Object.entries(customFilter).forEach(([key, value]) => {
        url.searchParams.append(key, String(value));
      });
    }
    const result = await this.fetcher(url.toString(), {
      method: "GET",
      headers: this.defaultHeaders,
    });
    const data = await result.json();
    return data.data;
  }

  async addOrderTag(orderId: string, tag: string): Promise<any> {
    const url = new URL(
      `${this.mainUrl}internal/inventory/order/${orderId}/tag`
    );
    const result = await this.fetcher(url.toString(), {
      method: "POST",
      headers: this.defaultHeaders,
      body: JSON.stringify({ tag }),
    });
    const data = await result.json();
    return data.data;
  }

  async removeOrderTag(orderId: string, tag: string): Promise<any> {
    const url = new URL(
      `${this.mainUrl}internal/inventory/order/${orderId}/tag`
    );
    const result = await this.fetcher(url.toString(), {
      method: "DELETE",
      headers: this.defaultHeaders,
      body: JSON.stringify({ tag }),
    });
    const data = await result.json();
    return data.data;
  }
}

export default new Api(apiUrl);
